// DEPENDENCIES
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { getAPICall, postAPICall, putAPICall } from "../../../config/apiCalls";

// MAPS
import { GoogleMap, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { AdvancedMarker, buildUnitPinElement } from "./AdvancedMarker";

// CSS
import "./PropertyViewer.css";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// CUSTOM COMPONENTS
import PropertyConsoleEditAddDialog from "../../Admin/PropertyConsole/PropertyConsoleEditAddDialog/PropertyConsoleEditAddDialog";
import DropboxFileSelector from "../../Misc/DropboxFileSelector/DropboxFileSelector";
import UnitMiningWizard from "./UnitMiningWizard";
import PropertyEnrichReviewDialog from "./PropertyEnrichReviewDialog";
import PropertyEnrichRunDialog from "./PropertyEnrichRunDialog";
import PropertyUnitDialog from "./PropertyUnitDialog";
import PropertyLinksDialog from "./PropertyLinksDialog";
import LinkedRecordEditDialog from "./LinkedRecordEditDialog";

// MUI COMPONENTS
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { debounce } from "@mui/material";

// MUI ICONS
import AddIcon from "@mui/icons-material/Add";
import AddLinkIcon from "@mui/icons-material/AddLink";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PlaceIcon from "@mui/icons-material/Place";
import LaunchIcon from "@mui/icons-material/Launch";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const notApplicable = "—";

// Stable references so @react-google-maps/api doesn't think the map container
// or requested libraries changed between renders (which would trigger
// pointless script reloads).
const GOOGLE_MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const GOOGLE_MAPS_LIBRARIES = ["marker"];

const TABS = [
	{ id: "overview", label: "Overview", sectionKey: "overview" },
	{ id: "jobs", label: "Jobs", sectionKey: "jobs" },
	{ id: "finances", label: "Finances", sectionKey: "finances" },
	{ id: "units", label: "Units", sectionKey: "units" },
	{ id: "contacts", label: "Contacts", sectionKey: "contacts" },
];

// Mirrors ProjectViewer's URL-driven selection: which property and which tab
// are showing live in the query string, so links (and the back button) work.
const PROPERTY_QUERY_PARAM = "recordId";
const TAB_QUERY_PARAM = "tab";
const DEFAULT_TAB_ID = "overview";
const VALID_TAB_IDS = TABS.map((t) => t.id);

// Six independently-fetchable sections (see propertyDetailService.js on the
// server) — fired in parallel and merged into `detail` as each resolves, so
// the loading bar reflects genuine per-section completion instead of one
// opaque combined request with no visible midpoint.
const PROPERTY_SECTION_CONFIG = [
	{ key: "shell", endpoint: "/api/jobs/property/shell" },
	{ key: "overview", endpoint: "/api/jobs/property/overview" },
	{ key: "jobs", endpoint: "/api/jobs/property/jobs" },
	{ key: "finances", endpoint: "/api/jobs/property/finances" },
	{ key: "units", endpoint: "/api/jobs/property/units" },
	{ key: "contacts", endpoint: "/api/jobs/property/contacts" },
];

// Shallow-merges a section's response into the accumulated detail object,
// except `property` — shell and overview each contribute different property
// fields, so that one sub-object needs a merge of its own or the later
// section to resolve would blow away the earlier one's fields.
const mergePropertySection = (current, sectionData) => {
	const merged = { ...(current || {}), ...sectionData };
	if (sectionData.property || current?.property) {
		merged.property = { ...(current?.property || {}), ...(sectionData.property || {}) };
	}
	return merged;
};

// Left column, HubSpot internal field name — the exact set requested off the
// property's custom HubSpot object (type 2-11693811). Property Details/Type/
// County/Phone Number stay visible by default; the rest sit behind a toggle.
const PRIMARY_DETAIL_FIELDS = [
	["Property Details", "property_details"],
	["Property Type", "building_type"],
	["County", "county"],
	["Phone Number", "phone_number"],
];
const SECONDARY_DETAIL_FIELDS = [
	["Year Built", "year_built"],
	["Number of Buildings", "number_of_buildings"],
	["Number of Stories", "number_of_stories"],
	["Number of Units", "number_of_units"],
	["Common Bldgs", "common_bldgs"],
	["Common Amenities", "common_amenitites"],
	["Cladding Type", "cladding_type"],
	["Roof Type", "roof_type"],
	["Decks", "decks"],
	["Ext Stairs", "ext_stairs"],
	["Carports", "carports"],
	["Garages", "garages"],
	["Neighborhood", "neighborhood"],
	["Parcel Number", "parcel_number"],
	["Plat Map URL", "plat_map_url"],
];

// Rendered as a clickable link in the read-only Property Details grid
// instead of raw URL text — every other field there is plain text, this is
// the only link-shaped one.
const DETAIL_FIELD_IS_URL = { plat_map_url: true };

// Both of these are fieldType "text" with HubSpot's own textDisplayHint
// "domain_name" — confirmed live that HubSpot rejects a save outright (400,
// "No protocol found. URL must start with 'http://' or 'https://'.") for
// anything without the scheme, a very easy mistake when pasting a bare
// domain or a URL copied out of a browser bar that hides it. Auto-prepending
// https:// before send covers that common case instead of surfacing a
// confusing 400 on save.
const URL_DETAIL_FIELD_KEYS = ["plat_map_url", "property_website_url"];
const normalizeUrlDraftValue = (key, value) => {
	const trimmed = (value || "").trim();
	if (!trimmed || !URL_DETAIL_FIELD_KEYS.includes(key)) return value;
	return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};
const SECONDARY_DETAIL_FIELDS_MIDPOINT = Math.ceil(SECONDARY_DETAIL_FIELDS.length / 2);
const SECONDARY_DETAIL_FIELDS_COL_1 = SECONDARY_DETAIL_FIELDS.slice(0, SECONDARY_DETAIL_FIELDS_MIDPOINT);
const SECONDARY_DETAIL_FIELDS_COL_2 = SECONDARY_DETAIL_FIELDS.slice(SECONDARY_DETAIL_FIELDS_MIDPOINT);

// Every field the Edit Property Details dialog can write back to HubSpot.
// Property Name isn't part of PRIMARY_DETAIL_FIELDS (which doubles as the
// read-only Overview grid) since the hero <h1> already shows it — it only
// needs to be editable here.
const EDITABLE_DETAIL_FIELDS = [["Property Name", "property_name"], ...PRIMARY_DETAIL_FIELDS, ...SECONDARY_DETAIL_FIELDS, ["Property Website", "property_website_url"]];

// The fields Enrich's description generation actually grounds on — keep this
// in sync with server/lib/propertyEnrichment/propertyEnrichmentService.js's
// SCALAR_FIELD_MAP/BOOL_FIELD_MAP/LIST_FIELD_MAP keys. Saving a change to any
// of these (not just any field in the edit dialog — Phone Number or Parcel
// Number, say, don't affect the description) is what prompts to regenerate it.
const ENRICHMENT_FIELD_KEYS = [
	"year_built", "number_of_buildings", "number_of_stories", "number_of_units",
	"cladding_type", "roof_type", "decks", "ext_stairs", "carports", "garages",
	"common_bldgs", "common_amenitites",
];
const DETAIL_FIELD_LABEL_BY_KEY = Object.fromEntries([...PRIMARY_DETAIL_FIELDS, ...SECONDARY_DETAIL_FIELDS].map(([label, key]) => [key, label]));

const FIN_TILES = [
	{ key: "contracted", label: "Contracted", note: "All jobs to date" },
	{ key: "billed", label: "Billed", note: "All invoices to date" },
	{ key: "outstanding", label: "Outstanding", note: "Open invoices" },
	{ key: "pastDue", label: "Past Due", note: "Overdue invoices" },
];

// Display order for the property header's job status breakdown — active work
// leads, terminal statuses trail.
const JOB_STATUS_ORDER = ["Active", "On Hold", "Completed", "Inactive", "Canceled"];

// Exact-match color classes for the header's job status breakdown. job.status
// is always one of these five known BQE labels — chipClassFor's fuzzy regex
// (built for messier status text elsewhere) misclassifies "Inactive" as
// "active" since it substring-matches /active/, so On Hold and Inactive need
// their own correct, distinct colors here instead of reusing it.
const JOB_STATUS_CLASS = {
	Active: "active",
	"On Hold": "hold",
	Completed: "complete",
	Inactive: "pending",
	Canceled: "overdue",
};

const chipClassFor = (status) => {
	const s = String(status || "").trim().toLowerCase();
	if (/complete|paid|closed|current|leased/.test(s)) return "complete";
	if (/hold|review|pending amend/.test(s)) return "hold";
	if (/overdue|delinquent|past due/.test(s)) return "overdue";
	if (/active|in progress|construction|open|owner occupied/.test(s)) return "active";
	return "pending";
};

const StatusChip = ({ status }) => (
	<span className={`propertyViewerChip ${chipClassFor(status)}`}>{status || notApplicable}</span>
);

// HubSpot models properties as a custom object (type 2-11693811), not a
// Company or Deal — see /api/hubspot/getPropertyFromPropertyID on the server.
const HUBSPOT_PROPERTY_RECORD_URL = (hubspotId) =>
	`https://app.hubspot.com/contacts/9144184/record/2-11693811/${hubspotId}`;

// Standard HubSpot Contact object (0-1).
const HUBSPOT_CONTACT_RECORD_URL = (contactId) => `https://app.hubspot.com/contacts/9144184/record/0-1/${contactId}`;

// HubSpot Listings object (0-420), used here for Units.
const HUBSPOT_UNIT_RECORD_URL = (unitId) => `https://app.hubspot.com/contacts/9144184/record/0-420/${unitId}`;

// Standard HubSpot Company object (0-2).
const HUBSPOT_COMPANY_RECORD_URL = (companyId) => `https://app.hubspot.com/contacts/9144184/record/0-2/${companyId}`;

// Entities custom object (2-11777488) — HOAs / owner LLCs / condo associations.
const HUBSPOT_ENTITY_RECORD_URL = (entityId) => `https://app.hubspot.com/contacts/9144184/record/2-11777488/${entityId}`;

// Mirrors ProjectViewer's Dropbox link builder.
const buildDropboxFolderUrl = (path) => {
	if (!path) return "";
	const normalizedPath = path.startsWith("/") ? path : `/${path}`;
	const encodedPath = normalizedPath
		.split("/")
		.map((segment) => encodeURIComponent(segment))
		.join("/");
	return `https://www.dropbox.com/home${encodedPath}`;
};

const buildBqeProjectUrl = (bqeId, tab = "overview") => {
	if (!bqeId) return "";
	return `https://wap1-01.bqecore.com/webapp/Project/Edit/${bqeId}?rootId=${bqeId}&ProjectAssignmentsDD=4&tab=${tab}`;
};

// Neither of these two is scoped to a specific property — they just open
// BQE's/HubSpot's general list screens for the user to search from there.
const BQE_INVOICE_REVIEW_URL = "https://wap1-01.bqecore.com/webapp/InvoiceReview/InvoiceReview?saveParameters=true";

// Same Quick Links a phase has in ProjectViewer (HubSpot Deal, BQE, Dropbox,
// CompanyCam) — hubspotDealUrl/companyCamUrl/dropboxUrl come from the server
// (propertyDetailService reuses the PM Dashboard's per-phase lookup); the BQE
// link is built client-side from the phase's own BQE id, same as the property's.
const buildPhaseQuickLinkItems = (ph) => [
	{ key: "hubspot", label: "HubSpot Deal", short: "HS", url: ph.hubspotDealUrl, badgeClass: "hubspot" },
	{ key: "bqe", label: "BQE Core", short: "BQ", url: buildBqeProjectUrl(ph.id), badgeClass: "bqe" },
	{ key: "dropbox", label: "Dropbox", short: "DB", url: ph.dropboxUrl, badgeClass: "dropbox" },
	{ key: "companycam", label: "CompanyCam", short: "CC", url: ph.companyCamUrl, badgeClass: "ccam" },
];

const formatCurrency = (value) => {
	const num = parseFloat(value);
	if (isNaN(num)) return notApplicable;
	return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
};

const formatDate = (value) => {
	if (!value) return notApplicable;
	const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
	if (isNaN(date.getTime())) return notApplicable;
	return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Matches ProjectViewer's "Property: Project: Phase" job naming convention.
const formatJobPath = (...segments) => segments.filter(Boolean).join(": ");

// property/update responds with the raw mysql error object at HTTP 200 on a
// DB failure (no thrown status), so putAPICall resolves normally — this
// checks the body's shape to tell a mysql error packet (has
// code/errno/sqlMessage) apart from a real OkPacket result.
const isDbErrorResponse = (data) =>
	Boolean(data) &&
	(data.errno !== undefined || data.sqlMessage !== undefined || data.code !== undefined);

// ─── REACT COMPONENT ────────────────────────────────────────────────────────

const PropertyViewer = (props) => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);
	const [searchParams, setSearchParams] = useSearchParams();

	// URL-driven state — mirrors ProjectViewer's recordId/tab query params.
	const idFromQuery = searchParams.get(PROPERTY_QUERY_PARAM) || "";
	const tabFromQuery = searchParams.get(TAB_QUERY_PARAM) || "";
	const activeTab = VALID_TAB_IDS.includes(tabFromQuery) ? tabFromQuery : DEFAULT_TAB_ID;

	const [toolsProperties, setToolsProperties] = useState([]);
	const [isListLoading, setIsListLoading] = useState(true);
	const [listError, setListError] = useState("");

	const [searchTerm, setSearchTerm] = useState("");
	const [officeFilter, setOfficeFilter] = useState("All Offices");
	const [browserPaneCollapsed, setBrowserPaneCollapsed] = useState(false);
	const [selectedId, setSelectedId] = useState(idFromQuery || null);
	const [openJobs, setOpenJobs] = useState({});
	const [mapMode, setMapMode] = useState("k");
	const [mapDialogOpen, setMapDialogOpen] = useState(false);
	const [detailsExpanded, setDetailsExpanded] = useState(false);
	const [addressCopied, setAddressCopied] = useState(false);
	const [copiedContactEmail, setCopiedContactEmail] = useState("");
	const [invoiceListTab, setInvoiceListTab] = useState("outstanding");
	const [invoicesSearch, setInvoicesSearch] = useState("");
	const [unitsSearch, setUnitsSearch] = useState("");
	// Add/Edit Unit dialog — editingUnit null while open means "add".
	const [unitDialogOpen, setUnitDialogOpen] = useState(false);
	const [editingUnit, setEditingUnit] = useState(null);
	const [contactsTabView, setContactsTabView] = useState("contacts");
	const [contactsSearch, setContactsSearch] = useState("");
	// Contacts tab dialogs — Manage Links (all linked records), and the
	// per-row Edit dialog ({ kind, record } of the row being edited).
	const [linksDialogOpen, setLinksDialogOpen] = useState(false);
	const [editingLinkedRecord, setEditingLinkedRecord] = useState(null);
	const [activeMapMarkerId, setActiveMapMarkerId] = useState(null);
	const [editDetailsOpen, setEditDetailsOpen] = useState(false);
	const [editDetailsDraft, setEditDetailsDraft] = useState({});
	// Snapshot of the enrichment-relevant fields' values as of when the edit
	// dialog opened, so saveEditDetails can tell which of them actually
	// changed (not just which fields the dialog happens to edit).
	const [editDetailsBaseline, setEditDetailsBaseline] = useState({});
	const [editDetailsSaving, setEditDetailsSaving] = useState(false);
	const [editDetailsError, setEditDetailsError] = useState("");
	const [addPropertyOpen, setAddPropertyOpen] = useState(false);

	// Shown after saving detail-field edits when an enrichment-relevant field
	// changed, asking whether to regenerate the description from the new
	// values — see saveEditDetails. changedFieldLabels drives the prompt's
	// "you changed X, Y" copy.
	const [enrichSavePromptOpen, setEnrichSavePromptOpen] = useState(false);
	const [enrichSavePromptFieldLabels, setEnrichSavePromptFieldLabels] = useState([]);

	// Enrich (Claude research + description generation against HubSpot) — see
	// server/lib/propertyEnrichment/propertyEnrichmentService.js. No manual/auto
	// tracking is kept; enrichChanges/enrichReviewOpen are the only safeguard,
	// populated whenever a candidate value differs from an existing non-blank field.
	const [enrichDialogOpen, setEnrichDialogOpen] = useState(false);
	const [enrichRunning, setEnrichRunning] = useState(null); // "full" | "research" | "description" | null
	const [lastEnrichMode, setLastEnrichMode] = useState(null); // remembers the mode for the review dialog's "Try Again"
	// postAPICall has no request-abort support, so cancelling/switching
	// properties only stops the UI from waiting on the response — the Claude
	// call already dispatched to the backend still runs to completion
	// server-side. Each run captures the current counter value as its own id;
	// bumping the counter (on cancel, or on switching to a different property)
	// invalidates every in-flight request at once, so a late response can
	// never apply on top of a newer run or a different property's page —
	// a single "cancelled" boolean can't do this correctly once more than one
	// request can be in flight (cancel one, start another, the first's late
	// response would otherwise still look "not cancelled" to the new run).
	const enrichRequestIdRef = useRef(0);
	const [enrichError, setEnrichError] = useState("");
	const [enrichReviewOpen, setEnrichReviewOpen] = useState(false);
	const [enrichChanges, setEnrichChanges] = useState([]);
	const [enrichReviewErrors, setEnrichReviewErrors] = useState([]); // informational (e.g. "research unavailable"), not fatal
	const [enrichSelections, setEnrichSelections] = useState({});
	const [enrichConfirmSaving, setEnrichConfirmSaving] = useState(false);

	// J2 Tools Details (admin-only) — Tools DB fields with no HubSpot
	// equivalent, edited alongside the HubSpot fields above but saved to
	// /api/jobs/property/update instead of /api/jobs/property/details.
	const [oicDraft, setOicDraft] = useState("");
	const [dropboxPathDraft, setDropboxPathDraft] = useState("");
	const [bqePropertyRecord, setBqePropertyRecord] = useState(null);
	const [bqePropertySearchValue, setBqePropertySearchValue] = useState("");
	const [bqePropertyOptions, setBqePropertyOptions] = useState([]);
	const [bqePropertySearchLoading, setBqePropertySearchLoading] = useState(false);
	const addressCopiedTimeoutRef = useRef(null);
	const copiedEmailTimeoutRef = useRef(null);

	const [detail, setDetail] = useState(null);
	const [sectionStatus, setSectionStatus] = useState({});
	const [detailError, setDetailError] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);

	const { isLoaded: isMapsLoaded, loadError: mapsLoadError } = useJsApiLoader({
		id: "j2-property-viewer-google-map",
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
		libraries: GOOGLE_MAPS_LIBRARIES,
	});

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Property Viewer",
			employeeID: employeeID,
			notes: "Accessed the Property Viewer page",
		}).catch(() => {});
	}, []);

	// ── Fetch the real property list (J2 Tools `properties` table) ──
	// Shared by the initial mount load and by a manual reload after a
	// property is added, so the new record shows up without a page refresh.
	const loadPropertyList = useCallback(() => {
		let cancelled = false;
		setIsListLoading(true);
		setListError("");
		getAPICall(instance, accounts[0], "/api/jobs/property/all")
			.then((res) => {
				if (cancelled) return;
				const shaped = (res.data || [])
					.map((p) => ({
						id: p.id,
						name: p.name,
						state: p.oic || "",
						hubspotId: p.hubspot_id || "",
						addressLine1: p.address_line1 || "",
						city: p.city || "",
					}))
					.filter((p) => p.name)
					.sort((a, b) => a.name.localeCompare(b.name));
				setToolsProperties(shaped);
			})
			.catch((err) => {
				if (cancelled) return;
				setListError(err?.response?.data?.message || err?.message || "Failed to load properties.");
			})
			.finally(() => {
				if (!cancelled) setIsListLoading(false);
			});
		return () => { cancelled = true; };
	}, [instance, accounts]);

	useEffect(() => loadPropertyList(), [loadPropertyList]);

	// ── Sync selectedId ↔ URL ────────────────────────────────────────────────
	useEffect(() => {
		if (idFromQuery && idFromQuery !== String(selectedId)) {
			setSelectedId(idFromQuery);
			return;
		}

		const nextParams = new URLSearchParams(searchParams);
		if (selectedId) {
			nextParams.set(PROPERTY_QUERY_PARAM, String(selectedId));
		} else {
			nextParams.delete(PROPERTY_QUERY_PARAM);
		}
		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [idFromQuery, selectedId, searchParams, setSearchParams]);

	// Guards against a slow section response from a property the user has
	// since navigated away from landing on the now-selected one — e.g.
	// switching from Property A to Property B while A's "units" request
	// (a live HubSpot associations lookup + geocoding, one of the slower
	// sections) is still in flight lets it resolve after B is already
	// selected; without this check its .then still fires and merges A's
	// units into B's `detail`, since mergePropertySection has no way to know
	// the response it's given is stale. Every request captures the id it was
	// issued for and compares against this ref (always the most recently
	// requested id) before touching state.
	const latestDetailRequestIdRef = useRef(null);

	// Loads (or reloads) all six property sections in parallel. bust=false is a
	// full load for a newly-selected property (clears detail first); bust=true
	// is a manual refresh of an already-loaded property, which re-fetches
	// everything without blanking what's currently on screen.
	const loadPropertyDetail = useCallback((id, { bust = false } = {}) => {
		latestDetailRequestIdRef.current = id;

		if (bust) {
			setIsRefreshing(true);
		} else {
			setDetail(null);
		}
		setDetailError("");
		setSectionStatus(
			PROPERTY_SECTION_CONFIG.reduce((acc, { key }) => {
				acc[key] = { loading: true, error: "" };
				return acc;
			}, {}),
		);

		const requests = PROPERTY_SECTION_CONFIG.map(({ key, endpoint }) =>
			getAPICall(instance, accounts[0], endpoint, { id })
				.then((res) => {
					if (latestDetailRequestIdRef.current !== id) return;
					setDetail((current) => mergePropertySection(current, res.data));
					setSectionStatus((current) => ({ ...current, [key]: { loading: false, error: "" } }));
				})
				.catch((err) => {
					if (latestDetailRequestIdRef.current !== id) return;
					const message = err?.response?.data?.error || err?.message || `Failed to load ${key}.`;
					setSectionStatus((current) => ({ ...current, [key]: { loading: false, error: message } }));
					// The shell section is just a DB lookup for the property's own
					// identity — if that fails, the property itself is the problem,
					// not one BQE/HubSpot sub-fetch, so surface it as a page error.
					if (key === "shell") setDetailError(message);
				}),
		);

		Promise.allSettled(requests).then(() => {
			if (latestDetailRequestIdRef.current === id && bust) setIsRefreshing(false);
		});
	}, [instance, accounts]);

	// ── Fetch property detail sections whenever the selected property changes ─
	useEffect(() => {
		if (!selectedId) {
			setDetail(null);
			setSectionStatus({});
			return;
		}
		loadPropertyDetail(selectedId, { bust: false });
	}, [selectedId, loadPropertyDetail]);

	// Enrich dialog state is keyed to whichever property it last ran against —
	// switching properties without this reset left a prior property's results
	// (or, worse, a still-in-flight request for it) able to resurface here
	// under an unrelated property. Invalidating any in-flight request the same
	// way the dialog's own Cancel button does prevents a late response from a
	// property the user has already navigated away from from applying here.
	useEffect(() => {
		enrichRequestIdRef.current += 1;
		setEnrichDialogOpen(false);
		setEnrichRunning(null);
		setLastEnrichMode(null);
		setEnrichError("");
		setEnrichReviewOpen(false);
		setEnrichChanges([]);
		setEnrichReviewErrors([]);
		setEnrichSelections({});
		setEnrichSavePromptOpen(false);
		setEnrichSavePromptFieldLabels([]);
		setUnitDialogOpen(false);
		setEditingUnit(null);
		setLinksDialogOpen(false);
		setEditingLinkedRecord(null);
	}, [selectedId]);

	const handleRefreshProperty = useCallback(() => {
		if (!selectedId || isRefreshing) return;
		loadPropertyDetail(selectedId, { bust: true });
	}, [selectedId, isRefreshing, loadPropertyDetail]);

	// Re-fetches just the units section after an add/edit, instead of all six
	// — the table keeps showing the current units until the new list lands
	// (sectionStatus.units.loading stays false so the tab doesn't swap to its
	// skeleton), and the same stale-property guard as loadPropertyDetail applies.
	const reloadUnits = useCallback(() => {
		const id = selectedId;
		if (!id) return;
		getAPICall(instance, accounts[0], "/api/jobs/property/units", { id })
			.then((res) => {
				if (latestDetailRequestIdRef.current !== id) return;
				setDetail((current) => mergePropertySection(current, res.data));
				setSectionStatus((current) => ({ ...current, units: { loading: false, error: "" } }));
			})
			.catch((err) => {
				if (latestDetailRequestIdRef.current !== id) return;
				const message = err?.response?.data?.error || err?.message || "Failed to load units.";
				setSectionStatus((current) => ({ ...current, units: { loading: false, error: message } }));
			});
	}, [selectedId, instance, accounts]);

	const openAddUnit = () => {
		setEditingUnit(null);
		setUnitDialogOpen(true);
	};

	const openEditUnit = (unit) => {
		setEditingUnit(unit);
		setUnitDialogOpen(true);
	};

	// The Contacts tab dialogs' save responses carry the refreshed contacts
	// section ({ contacts, companies, entities }) — merge it straight in
	// rather than re-fetching. `forPropertyId` is the property the dialog was
	// saving for — a save still in flight when the user switches properties
	// must not land on the newly selected one.
	const handleContactsSectionSaved = (forPropertyId, section) => {
		if (String(latestDetailRequestIdRef.current) !== String(forPropertyId)) return;
		setDetail((current) =>
			mergePropertySection(current, { contacts: section.contacts, companies: section.companies, entities: section.entities }),
		);
	};

	const officeOptions = useMemo(
		() => ["All Offices", ...Array.from(new Set(toolsProperties.map((p) => p.state))).filter(Boolean).sort()],
		[toolsProperties],
	);

	const filteredProperties = useMemo(
		() =>
			toolsProperties.filter((p) => {
				const matchesOffice = officeFilter === "All Offices" || p.state === officeFilter;
				const term = searchTerm.trim().toLowerCase();
				const matchesSearch =
					term === "" ||
					p.name.toLowerCase().includes(term) ||
					p.addressLine1.toLowerCase().includes(term) ||
					p.city.toLowerCase().includes(term);
				return matchesOffice && matchesSearch;
			}),
		[toolsProperties, searchTerm, officeFilter],
	);

	// The real selected property (drives the hero header + list highlighting).
	const toolsProperty = toolsProperties.find((p) => String(p.id) === String(selectedId)) || null;
	const hubspotUrl = toolsProperty?.hubspotId ? HUBSPOT_PROPERTY_RECORD_URL(toolsProperty.hubspotId) : "";
	const dropboxUrl = detail?.property?.dropboxPath ? buildDropboxFolderUrl(detail.property.dropboxPath) : "";
	const bqeUrl = detail?.property?.bqeId ? buildBqeProjectUrl(detail.property.bqeId) : "";
	const bqeStructureUrl = detail?.property?.bqeId ? buildBqeProjectUrl(detail.property.bqeId, "structure") : "";
	const websiteUrl = detail?.hubspotFields?.property_website_url || "";

	const quickLinkItems = [
		{ key: "hubspot", label: "HubSpot", short: "HS", url: hubspotUrl, badgeClass: "hubspot" },
		{ key: "bqe", label: "BQE Core", short: "BQ", url: bqeUrl, badgeClass: "bqe" },
		{ key: "dropbox", label: "Dropbox", short: "DB", url: dropboxUrl, badgeClass: "dropbox" },
		{ key: "website", label: "Property Website", short: "WWW", url: websiteUrl, badgeClass: "website" },
	];

	const renderQuickLinkIcons = () => (
		<div className="propertyViewerHeroQuickLinks">
			{quickLinkItems.map((item) => (
				<a
					key={item.key}
					href={item.url || undefined}
					target={item.url ? "_blank" : undefined}
					rel={item.url ? "noopener noreferrer" : undefined}
					className={`propertyViewerIntegrationBadge propertyViewerHeroLinkBadge ${item.badgeClass}${!item.url ? " disabled" : ""}`}
					title={item.url ? item.label : `${item.label} Unavailable`}
					aria-label={item.label}
				>
					{item.short}
				</a>
			))}
		</div>
	);

	const currentIndex = filteredProperties.findIndex((p) => String(p.id) === String(selectedId));
	const prevProperty = currentIndex > 0 ? filteredProperties[currentIndex - 1] : null;
	const nextProperty =
		currentIndex !== -1 && currentIndex < filteredProperties.length - 1
			? filteredProperties[currentIndex + 1]
			: null;

	const setActiveTab = useCallback(
		(tabId) => {
			const nextParams = new URLSearchParams(searchParams);
			nextParams.set(TAB_QUERY_PARAM, tabId);
			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const selectProperty = (id) => {
		setSelectedId(String(id));
		setOpenJobs({});
		setDetailsExpanded(false);
		setMapDialogOpen(false);
		setActiveMapMarkerId(null);

		// Set recordId in the same tick as setSelectedId, so idFromQuery and
		// selectedId land in sync on the next render. Otherwise the "Sync
		// selectedId ↔ URL" effect below sees a stale idFromQuery that doesn't
		// match the just-clicked id yet and reverts selectedId back to whatever
		// property was already in the URL. The tab param is left as-is so the
		// user stays on the same tab when switching properties (same as
		// ProjectViewer).
		const nextParams = new URLSearchParams(searchParams);
		if (id) {
			nextParams.set(PROPERTY_QUERY_PARAM, String(id));
		} else {
			nextParams.delete(PROPERTY_QUERY_PARAM);
		}
		setSearchParams(nextParams, { replace: true });
	};

	const toggleJob = (index) => {
		setOpenJobs((current) => ({ ...current, [index]: !current[index] }));
	};

	const closeAddProperty = () => setAddPropertyOpen(false);

	const closeAddPropertyAndRefresh = () => {
		setAddPropertyOpen(false);
		loadPropertyList();
	};

	// Cleanup the "Copied!" timeouts on unmount.
	useEffect(() => () => clearTimeout(addressCopiedTimeoutRef.current), []);
	useEffect(() => () => clearTimeout(copiedEmailTimeoutRef.current), []);

	const handleCopyAddress = (address) => {
		if (!address || !navigator.clipboard) return;
		navigator.clipboard.writeText(address).then(() => {
			setAddressCopied(true);
			clearTimeout(addressCopiedTimeoutRef.current);
			addressCopiedTimeoutRef.current = setTimeout(() => setAddressCopied(false), 1500);
		});
	};

	const handleCopyEmail = (email) => {
		if (!email || !navigator.clipboard) return;
		navigator.clipboard.writeText(email).then(() => {
			setCopiedContactEmail(email);
			clearTimeout(copiedEmailTimeoutRef.current);
			copiedEmailTimeoutRef.current = setTimeout(() => setCopiedContactEmail(""), 1500);
		});
	};

	const hubspotFields = detail?.hubspotFields || {};

	// Shared by every read-only Property Details grid cell (Primary + both
	// Secondary columns) — plain text for every field except plat_map_url,
	// which is only useful as a clickable link to the county plat map. This
	// field is free-text editable (same TextField every other detail field
	// gets, via EDITABLE_DETAIL_FIELDS), so it's just as available to a
	// javascript:-scheme value as any other injection point — only ever
	// render it as a real <a href> when it parses as an actual http(s) URL;
	// anything else still shows as plain (inert) text rather than a link.
	const renderDetailValue = (key) => {
		const value = hubspotFields[key];
		if (!value) return notApplicable;
		if (DETAIL_FIELD_IS_URL[key]) {
			let isSafeUrl = false;
			try {
				isSafeUrl = ["http:", "https:"].includes(new URL(value).protocol);
			} catch {
				isSafeUrl = false;
			}
			if (isSafeUrl) {
				return (
					<a href={value} target="_blank" rel="noopener noreferrer">
						{value}
					</a>
				);
			}
		}
		return value;
	};

	// Property Name is admin-only — non-admins get the rest of the HubSpot
	// fields as before, just without the ability to rename the property.
	const editableDetailFields = props.isAdmin
		? EDITABLE_DETAIL_FIELDS
		: EDITABLE_DETAIL_FIELDS.filter(([, key]) => key !== "property_name");

	const bqePropertyNameChanged = useMemo(
		() =>
			debounce((event) => {
				const searchValue = event.target.value;
				setBqePropertySearchLoading(true);
				if (searchValue.length < 3) {
					setBqePropertySearchLoading(false);
					setBqePropertyOptions([]);
					return;
				}
				getAPICall(instance, accounts[0], "/api/bqe/property/byName", { propertyName: searchValue })
					.then((res) => {
						setBqePropertyOptions(
							res.data === "" ? [] : res.data.filter((option) => !option.displayName.endsWith("(deleted)")),
						);
					})
					.catch(() => setBqePropertyOptions([]))
					.finally(() => setBqePropertySearchLoading(false));
			}, 500),
		[instance, accounts],
	);

	const bqePropertyNameSelected = (event, newValue) => {
		if (newValue && newValue.displayName) {
			setBqePropertyRecord(newValue);
			setBqePropertySearchValue(newValue.displayName);
		} else {
			setBqePropertyRecord(null);
			setBqePropertySearchValue("");
			setBqePropertyOptions([]);
		}
	};

	const openEditDetails = () => {
		const draft = {};
		editableDetailFields.forEach(([, key]) => { draft[key] = hubspotFields[key] || ""; });
		setEditDetailsDraft(draft);
		setEditDetailsBaseline(Object.fromEntries(ENRICHMENT_FIELD_KEYS.map((key) => [key, hubspotFields[key] || ""])));
		setEditDetailsError("");
		setOicDraft(detail?.property?.oic || "");
		setDropboxPathDraft(detail?.property?.dropboxPath || "");
		setBqePropertyRecord(null);
		setBqePropertySearchValue("");
		setBqePropertyOptions([]);
		setEditDetailsOpen(true);

		// Prefill the BQE Autocomplete with whatever job this property is
		// already linked to, so admins see the current value instead of an
		// empty search box they'd have to re-search to confirm.
		if (props.isAdmin && detail?.property?.bqeId) {
			getAPICall(instance, accounts[0], "/api/bqe/property/byID", { id: detail.property.bqeId })
				.then((res) => {
					if (!res.data) return;
					setBqePropertyRecord(res.data);
					setBqePropertySearchValue(res.data.displayName || "");
				})
				.catch(() => {});
		}
	};

	const closeEditDetails = () => {
		if (editDetailsSaving) return;
		setEditDetailsOpen(false);
	};

	const saveEditDetails = () => {
		if (!toolsProperty?.hubspotId || editDetailsSaving) return;
		setEditDetailsSaving(true);
		setEditDetailsError("");
		const normalizedProperties = Object.fromEntries(
			Object.entries(editDetailsDraft).map(([key, value]) => [key, normalizeUrlDraftValue(key, value)]),
		);
		putAPICall(instance, accounts[0], "/api/jobs/property/details", {
			hubspotId: toolsProperty.hubspotId,
			properties: normalizedProperties,
		})
			.then((res) => {
				const nextHubspotFields = { ...detail?.hubspotFields, ...res.data.properties };
				setDetail((current) => mergePropertySection(current, { hubspotFields: nextHubspotFields }));

				if (!props.isAdmin) return null;

				// OIC/BQE/Dropbox (and any Property Name change) live in the
				// Tools DB, not HubSpot — resync them here so the rest of the
				// app (hero title, sidebar list, job/invoice paths, BQE job
				// lookups keyed off bqe_id) reflects what was just saved. The
				// HubSpot link itself (hubspotID) is intentionally unchanged.
				const nextName = (nextHubspotFields.property_name || toolsProperty.name || "").trim();
				return putAPICall(instance, accounts[0], "/api/jobs/property/update", {
					id: toolsProperty.id,
					name: nextName,
					addressLine1: nextHubspotFields.property_address_1,
					addressLine2: nextHubspotFields.property_address_2,
					city: nextHubspotFields.property_city,
					state: nextHubspotFields.property_state,
					zip: nextHubspotFields.property_zip_code,
					dropboxFilePath: dropboxPathDraft || null,
					bqeID: bqePropertyRecord ? bqePropertyRecord.id : null,
					bqeCode: bqePropertyRecord ? bqePropertyRecord.code : null,
					oic: oicDraft,
					modifiedBy: employeeID,
					hubspotID: toolsProperty.hubspotId,
				}).then((toolsRes) => {
					if (isDbErrorResponse(toolsRes.data)) {
						throw new Error(toolsRes.data.sqlMessage || toolsRes.data.code || "Failed to save J2 Tools details.");
					}
					setToolsProperties((current) =>
						current
							.map((p) => (String(p.id) === String(toolsProperty.id) ? { ...p, name: nextName } : p))
							.sort((a, b) => a.name.localeCompare(b.name)),
					);
					setDetail((current) =>
						mergePropertySection(current, {
							property: {
								oic: oicDraft,
								bqeId: bqePropertyRecord ? bqePropertyRecord.id : null,
								dropboxPath: dropboxPathDraft || null,
							},
						}),
					);
				});
			})
			.then(() => {
				// Only reached once the whole save has genuinely succeeded — for
				// admins that includes the Tools DB resync above. Checking here
				// rather than right after the HubSpot save means a failed resync
				// leaves the edit dialog (and its error) visible instead of it
				// being hidden behind this prompt. Only prompt when a field the
				// description actually grounds on changed — editing Phone Number
				// or Parcel Number, say, has no bearing on it. Accepting runs the
				// exact same flow as clicking "Regenerate Description" from the
				// Enrich dialog (loading state, then the review dialog every
				// time, even with zero changes).
				const changedEnrichmentKeys = ENRICHMENT_FIELD_KEYS.filter(
					(key) => (editDetailsDraft[key] ?? "").trim() !== (editDetailsBaseline[key] ?? "").trim(),
				);
				if (changedEnrichmentKeys.length) {
					setEnrichSavePromptFieldLabels(changedEnrichmentKeys.map((key) => DETAIL_FIELD_LABEL_BY_KEY[key] || key));
					setEnrichSavePromptOpen(true);
				} else {
					setEditDetailsOpen(false);
				}
			})
			.catch((err) => {
				setEditDetailsError(err?.response?.data?.error || err?.message || "Failed to save changes.");
			})
			.finally(() => setEditDetailsSaving(false));
	};

	// No stored data yet (blank Property Details) means Enrich hasn't run
	// before — this is derived fresh from the current field, not a stored
	// flag, matching the "no tracking metadata" design.
	const isFirstEnrich = !hubspotFields.property_details;

	// Polling interval for the enrichment job started below — the run itself
	// happens server-side in the background (see POST /property/enrich), so
	// this just checks in periodically rather than holding one HTTP request
	// open for however long Claude takes (which was long enough in practice
	// to hit Azure's front-end idle timeout and come back as a 504).
	const ENRICH_POLL_INTERVAL_MS = 3000;

	const pollEnrichJob = (jobId, requestId) => {
		if (enrichRequestIdRef.current !== requestId) return;
		getAPICall(instance, accounts[0], `/api/jobs/property/enrich/status/${jobId}`)
			.then((res) => {
				if (enrichRequestIdRef.current !== requestId) return;
				if (res.data.status === "running") {
					setTimeout(() => pollEnrichJob(jobId, requestId), ENRICH_POLL_INTERVAL_MS);
					return;
				}
				// A job that errored server-side comes back as a non-2xx response
				// (see the .catch below) — reaching here means status is "done".
				// Nothing is written yet at this point — every run always opens the
				// review dialog, even with zero proposed changes, so the user can
				// see what Claude found (or that nothing changed) before anything
				// is pushed to HubSpot.
				setEnrichChanges(res.data.changes || []);
				setEnrichSelections(Object.fromEntries((res.data.changes || []).map((c) => [c.key, "new"])));
				setEnrichReviewErrors(res.data.errors || []);
				setEnrichDialogOpen(false);
				setEnrichReviewOpen(true);
				setEnrichRunning(null);
			})
			.catch((err) => {
				if (enrichRequestIdRef.current !== requestId) return;
				setEnrichError(err?.response?.data?.error || err?.message || "Enrichment failed.");
				setEnrichRunning(null);
			});
	};

	const runEnrich = (mode) => {
		if (!toolsProperty?.hubspotId || enrichRunning) return;
		const requestId = ++enrichRequestIdRef.current;
		setLastEnrichMode(mode);
		setEnrichRunning(mode);
		setEnrichError("");
		postAPICall(instance, accounts[0], "/api/jobs/property/enrich", { id: toolsProperty.id, mode })
			.then((res) => {
				if (enrichRequestIdRef.current !== requestId) return;
				pollEnrichJob(res.data.jobId, requestId);
			})
			.catch((err) => {
				if (enrichRequestIdRef.current !== requestId) return;
				setEnrichError(err?.response?.data?.error || err?.message || "Enrichment failed.");
				setEnrichRunning(null);
			});
	};

	const closeEnrichRunDialog = () => {
		if (enrichRunning) return;
		setEnrichDialogOpen(false);
		setEnrichError("");
	};

	const declineEnrichSavePrompt = () => {
		setEnrichSavePromptOpen(false);
		setEditDetailsOpen(false);
	};

	const acceptEnrichSavePrompt = () => {
		setEnrichSavePromptOpen(false);
		setEditDetailsOpen(false);
		setEnrichDialogOpen(true);
		runEnrich("description");
	};

	// The Cancel button shown while a run is in flight — distinct from
	// closeEnrichRunDialog, which refuses to close while enrichRunning is set.
	const cancelEnrichRun = () => {
		enrichRequestIdRef.current += 1;
		setEnrichRunning(null);
		setEnrichDialogOpen(false);
		setEnrichError("");
	};

	const closeEnrichReview = () => {
		if (enrichConfirmSaving) return;
		setEnrichReviewOpen(false);
	};

	const retryEnrichReview = () => {
		if (enrichConfirmSaving || !lastEnrichMode) return;
		setEnrichReviewOpen(false);
		setEnrichDialogOpen(true);
		runEnrich(lastEnrichMode);
	};

	const confirmEnrichReview = () => {
		if (!toolsProperty?.hubspotId || enrichConfirmSaving) return;
		setEnrichConfirmSaving(true);
		setEnrichError("");
		const resolvedFields = {};
		const resolvedCompanies = [];
		enrichChanges.forEach((c) => {
			// "old" means skip this change entirely — for a conflict that's
			// leaving the existing HubSpot value untouched; for an addition
			// it's just not adding it. Either way nothing to write.
			if ((enrichSelections[c.key] === "old" ? "old" : "new") !== "new") return;
			if (c.kind === "company") {
				resolvedCompanies.push({ slot: c.slot, candidate: c.candidate, existingCompanyId: c.existingCompanyId || null });
			} else {
				// writeValue carries the real HubSpot-storable value for
				// boolean/list fields, whose oldValue/newValue are display text
				// ("Yes"/"No", comma-joined) — plain text fields have no
				// writeValue, so newValue is already the storable value.
				resolvedFields[c.key] = c.writeValue ? c.writeValue.new : c.newValue;
			}
		});
		if (!Object.keys(resolvedFields).length && !resolvedCompanies.length) {
			setEnrichReviewOpen(false);
			setEnrichConfirmSaving(false);
			return;
		}
		putAPICall(instance, accounts[0], "/api/jobs/property/enrich/confirm", {
			id: toolsProperty.id,
			hubspotId: toolsProperty.hubspotId,
			resolvedFields,
			resolvedCompanies,
		})
			.then((res) => {
				setDetail((current) => mergePropertySection(current, { hubspotFields: { ...current?.hubspotFields, ...res.data.properties } }));
				setEnrichReviewOpen(false);
				if (Object.keys(res.data.companyResults || {}).length) loadPropertyDetail(selectedId, { bust: true });
			})
			.catch((err) => setEnrichError(err?.response?.data?.error || err?.message || "Failed to save selections."))
			.finally(() => setEnrichConfirmSaving(false));
	};

	const jobs = detail?.jobs || [];
	const jobStatusCounts = jobs.reduce((acc, job) => {
		const status = job.status || "Unknown";
		acc[status] = (acc[status] || 0) + 1;
		return acc;
	}, {});
	const jobStatusBreakdown = JOB_STATUS_ORDER
		.filter((status) => jobStatusCounts[status])
		.map((status) => ({ status, count: jobStatusCounts[status] }));
	const finances = detail?.finances || null;
	// Paid = amount collected so far (amount − balance) per phase, from the
	// same invoice list the Finances tab uses — drafts haven't been billed
	// yet, so they don't count toward money actually paid.
	const paidByPhaseId = (finances?.invoices || []).reduce((acc, inv) => {
		if (inv.status === "Draft" || !inv.phaseId) return acc;
		acc[inv.phaseId] = (acc[inv.phaseId] || 0) + ((parseFloat(inv.amount) || 0) - (parseFloat(inv.balance) || 0));
		return acc;
	}, {});
	const units = detail?.units || [];
	const contacts = detail?.contacts || [];
	const companies = detail?.companies || [];
	const entities = detail?.entities || [];

	const unitsSearchTerm = unitsSearch.trim().toLowerCase();
	const filteredUnits = !unitsSearchTerm
		? units
		: units.filter(
			(u) =>
				(u.name || "").toLowerCase().includes(unitsSearchTerm) ||
				(u.contacts || []).some((c) => (c.name || "").toLowerCase().includes(unitsSearchTerm)) ||
				(u.entities || []).some((e) => (e.name || "").toLowerCase().includes(unitsSearchTerm)),
		);

	const contactsSearchTerm = contactsSearch.trim().toLowerCase();
	const filteredContacts = !contactsSearchTerm
		? contacts
		: contacts.filter(
			(ct) =>
				(ct.name || "").toLowerCase().includes(contactsSearchTerm) ||
				(ct.role || "").toLowerCase().includes(contactsSearchTerm) ||
				(ct.jobTitle || "").toLowerCase().includes(contactsSearchTerm) ||
				(ct.email || "").toLowerCase().includes(contactsSearchTerm) ||
				(ct.phone || "").toLowerCase().includes(contactsSearchTerm) ||
				(ct.mobilePhone || "").toLowerCase().includes(contactsSearchTerm),
		);
	const filteredCompanies = !contactsSearchTerm
		? companies
		: companies.filter(
			(c) =>
				(c.name || "").toLowerCase().includes(contactsSearchTerm) ||
				(c.role || "").toLowerCase().includes(contactsSearchTerm) ||
				(c.type || "").toLowerCase().includes(contactsSearchTerm) ||
				(c.domain || "").toLowerCase().includes(contactsSearchTerm) ||
				(c.phone || "").toLowerCase().includes(contactsSearchTerm),
		);
	const filteredEntities = !contactsSearchTerm
		? entities
		: entities.filter(
			(e) =>
				(e.name || "").toLowerCase().includes(contactsSearchTerm) ||
				(e.role || "").toLowerCase().includes(contactsSearchTerm) ||
				(e.type || "").toLowerCase().includes(contactsSearchTerm) ||
				[e.address, e.city, e.state, e.zip].filter(Boolean).join(", ").toLowerCase().includes(contactsSearchTerm) ||
				(e.phone || "").toLowerCase().includes(contactsSearchTerm),
		);

	const loadedSectionCount = PROPERTY_SECTION_CONFIG.filter((c) => !sectionStatus[c.key]?.loading).length;
	const loadProgress = (loadedSectionCount / PROPERTY_SECTION_CONFIG.length) * 100;
	const allSectionsLoaded = loadedSectionCount === PROPERTY_SECTION_CONFIG.length;

	const addressLine = [hubspotFields.property_address_1, hubspotFields.property_address_2].filter(Boolean).join(", ");
	const cityStateZip = [hubspotFields.property_city, hubspotFields.property_state].filter(Boolean).join(", ")
		+ (hubspotFields.property_zip_code ? ` ${hubspotFields.property_zip_code}` : "");
	const fullAddress = [addressLine, cityStateZip].filter(Boolean).join(", ");
	const mapQuery = encodeURIComponent(fullAddress || toolsProperty?.name || "");
	const gmapsUri = hubspotFields.gmaps_uri || (fullAddress ? `https://www.google.com/maps/search/?api=1&query=${mapQuery}` : "");

	const propertyLocation = detail?.property?.location || null;
	const unitMarkers = units.filter((u) => u.location);
	const mapCenter = propertyLocation || (unitMarkers[0] && unitMarkers[0].location) || null;

	// Units that geocode to (near enough) the same point — e.g. every unit in
	// a building sharing one street address — get combined into a single
	// marker instead of stacking identical pins on top of each other.
	const unitMarkerGroups = [];
	{
		const groupIndexByKey = new Map();
		unitMarkers.forEach((u, i) => {
			const key = `${u.location.lat.toFixed(5)},${u.location.lng.toFixed(5)}`;
			if (groupIndexByKey.has(key)) {
				unitMarkerGroups[groupIndexByKey.get(key)].units.push(u);
			} else {
				groupIndexByKey.set(key, unitMarkerGroups.length);
				unitMarkerGroups.push({ key, location: u.location, units: [u], firstIndex: i });
			}
		});
	}

	const renderPropertyMap = () => {
		if (mapsLoadError) {
			return <div className="propertyViewerEmptyStateMessage">Failed to load Google Maps.</div>;
		}
		if (!isMapsLoaded) {
			return <div className="propertyViewerEmptyStateMessage">Loading map…</div>;
		}
		if (!mapCenter) {
			return <div className="propertyViewerEmptyStateMessage">No address on file to show a map.</div>;
		}
		const mapTypeId = mapMode === "k" ? "satellite" : "roadmap";
		return (
			<GoogleMap
				mapContainerStyle={GOOGLE_MAP_CONTAINER_STYLE}
				center={mapCenter}
				zoom={17}
				mapTypeId={mapTypeId}
				// @react-google-maps/api constructs the underlying google.maps.Map
				// using only the `options` prop, then applies `mapTypeId` a tick
				// later as a separate update — baking it into `options` too means
				// the map is CREATED as satellite/roadmap, not switched after
				// briefly rendering the default (roadmap).
				options={{
					streetViewControl: false,
					fullscreenControl: false,
					mapTypeControl: false,
					tiltControl: false,
					rotateControl: false,
					tilt: 0,
					mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
					mapTypeId,
				}}
			>
				{propertyLocation && (
					<AdvancedMarker
						position={propertyLocation}
						// No content override — AdvancedMarkerElement renders its own
						// default pin automatically, distinct from our custom unit pins.
						title={toolsProperty?.name || "Property"}
						onClick={() => setActiveMapMarkerId("property")}
					>
						{activeMapMarkerId === "property" && (
							<InfoWindow onCloseClick={() => setActiveMapMarkerId(null)}>
								<div>
									<strong>{toolsProperty?.name}</strong>
									{fullAddress && <div>{fullAddress}</div>}
								</div>
							</InfoWindow>
						)}
					</AdvancedMarker>
				)}
				{unitMarkerGroups.map((group) => {
					const isCluster = group.units.length > 1;
					const pinContent = buildUnitPinElement({
						color: isCluster ? "#f57c00" : "#1976d2",
						label: isCluster ? String(group.units.length) : "",
						diameter: isCluster ? 26 : 20,
					});
					return (
						<AdvancedMarker
							key={group.key}
							position={group.location}
							content={pinContent}
							title={isCluster ? `${group.units.length} units at this address` : (group.units[0].name || `Unit ${group.firstIndex + 1}`)}
							onClick={() => setActiveMapMarkerId(group.key)}
						>
							{activeMapMarkerId === group.key && (
								<InfoWindow onCloseClick={() => setActiveMapMarkerId(null)}>
									<div className="propertyViewerMapInfoWindow">
										{group.units.map((u, i) => (
											<div className="propertyViewerMapInfoWindowUnit" key={u.id}>
												<strong>{u.name || `Unit ${group.firstIndex + i + 1}`}</strong>
												<div>{[u.address, u.city, u.state, u.zip].filter(Boolean).join(", ")}</div>
											</div>
										))}
									</div>
								</InfoWindow>
							)}
						</AdvancedMarker>
					);
				})}
			</GoogleMap>
		);
	};

	const renderOverviewTab = () => (
		<div className="propertyViewerTabPanel">
			<div className="propertyViewerMainGrid">
				<div className="propertyViewerPaper">
					<div className="propertyViewerPaperHeader propertyViewerCardHeaderWithAction">
						<h3>Property Details</h3>
						<div className="propertyViewerCardHeaderActions">
							<Button
								size="small"
								variant="outlined"
								startIcon={<AutoAwesomeIcon />}
								onClick={() => setEnrichDialogOpen(true)}
								disabled={!toolsProperty?.hubspotId}
								title={toolsProperty?.hubspotId ? undefined : "No HubSpot record linked to this property"}
							>
								Enrich
							</Button>
							<Button
								size="small"
								variant="outlined"
								startIcon={<EditIcon />}
								onClick={openEditDetails}
								disabled={!toolsProperty?.hubspotId}
								title={toolsProperty?.hubspotId ? undefined : "No HubSpot record linked to this property"}
							>
								Edit
							</Button>
						</div>
					</div>
					<div className="propertyViewerPaperBody">
						{sectionStatus.overview?.loading ? (
							<div className="propertyViewerEmptyStateMessage">Loading…</div>
						) : !detail?.property?.hubspotId ? (
							<div className="propertyViewerEmptyStateMessage">No HubSpot record linked to this property yet.</div>
						) : (
							<>
								<div className="propertyViewerKeyValueGrid">
									{PRIMARY_DETAIL_FIELDS.map(([label, key]) => (
										<React.Fragment key={key}>
											<div className="propertyViewerKey">{label}</div>
											<div className="propertyViewerValue">{renderDetailValue(key)}</div>
										</React.Fragment>
									))}
								</div>
								<button
									type="button"
									className={`propertyViewerCollapseToggle${detailsExpanded ? " open" : ""}`}
									onClick={() => setDetailsExpanded((current) => !current)}
								>
									<span className="propertyViewerCollapseLabel">
										{detailsExpanded ? "Hide" : "Show"} More Details
									</span>
									<ExpandMoreIcon fontSize="inherit" />
								</button>
								{detailsExpanded && (
									<div className="propertyViewerKeyValueGridSecondaryWrap">
										<div className="propertyViewerKeyValueGrid">
											{SECONDARY_DETAIL_FIELDS_COL_1.map(([label, key]) => (
												<React.Fragment key={key}>
													<div className="propertyViewerKey">{label}</div>
													<div className="propertyViewerValue">{renderDetailValue(key)}</div>
												</React.Fragment>
											))}
										</div>
										<div className="propertyViewerKeyValueGrid">
											{SECONDARY_DETAIL_FIELDS_COL_2.map(([label, key]) => (
												<React.Fragment key={key}>
													<div className="propertyViewerKey">{label}</div>
													<div className="propertyViewerValue">{renderDetailValue(key)}</div>
												</React.Fragment>
											))}
										</div>
									</div>
								)}
							</>
						)}
					</div>
				</div>

				<div className="propertyViewerStack">
					<div className="propertyViewerPaper">
						<div className="propertyViewerPaperHeader propertyViewerCardHeaderWithAction">
							<h3>Location</h3>
							<ToggleButtonGroup
								value={mapMode}
								exclusive
								size="small"
								color="primary"
								onChange={(e, val) => { if (val) setMapMode(val); }}
							>
								<ToggleButton value="m">Map</ToggleButton>
								<ToggleButton value="k">Satellite</ToggleButton>
							</ToggleButtonGroup>
						</div>
						<div className="propertyViewerMapFrame">
							{renderPropertyMap()}
							<IconButton
								size="small"
								className="propertyViewerMapExpandButton"
								onClick={() => { setActiveMapMarkerId(null); setMapDialogOpen(true); }}
								aria-label="Expand map"
								title="Expand map"
							>
								<FullscreenIcon fontSize="small" />
							</IconButton>
						</div>
						<div className="propertyViewerMapFooter">
							<div
								className={`propertyViewerMapAddress${fullAddress ? " propertyViewerCopyable" : ""}`}
								onClick={() => fullAddress && handleCopyAddress(fullAddress)}
								onKeyDown={(e) => { if (fullAddress && e.key === "Enter") handleCopyAddress(fullAddress); }}
								role={fullAddress ? "button" : undefined}
								tabIndex={fullAddress ? 0 : undefined}
								title={fullAddress ? "Click to copy address" : undefined}
							>
								{fullAddress || "Address not on file"}
								{addressCopied && <span className="propertyViewerCopiedBadge">Copied!</span>}
							</div>
							{gmapsUri && (
								<a
									href={gmapsUri}
									target="_blank"
									rel="noopener noreferrer"
									className="propertyViewerTableLink"
								>
									<PlaceIcon fontSize="inherit" /> Google Maps
								</a>
							)}
						</div>
					</div>

					<Dialog
						open={mapDialogOpen}
						onClose={() => { setActiveMapMarkerId(null); setMapDialogOpen(false); }}
						fullWidth
						maxWidth="lg"
						PaperProps={{ className: "propertyViewerMapDialogPaper" }}
					>
						<div className="propertyViewerMapDialogHeader">
							<div>
								<h3>{toolsProperty?.name}</h3>
							</div>
							<div className="propertyViewerMapDialogActions">
								<ToggleButtonGroup
									value={mapMode}
									exclusive
									size="small"
									color="primary"
									onChange={(e, val) => { if (val) setMapMode(val); }}
								>
									<ToggleButton value="m">Map</ToggleButton>
									<ToggleButton value="k">Satellite</ToggleButton>
								</ToggleButtonGroup>
								<IconButton onClick={() => { setActiveMapMarkerId(null); setMapDialogOpen(false); }} aria-label="Close">
									<CloseIcon />
								</IconButton>
							</div>
						</div>
						<div className="propertyViewerMapDialogFrame">
							{mapDialogOpen && renderPropertyMap()}
						</div>
						<div className="propertyViewerMapFooter propertyViewerMapDialogFooter">
							<div
								className={`propertyViewerMapAddress${fullAddress ? " propertyViewerCopyable" : ""}`}
								onClick={() => fullAddress && handleCopyAddress(fullAddress)}
								onKeyDown={(e) => { if (fullAddress && e.key === "Enter") handleCopyAddress(fullAddress); }}
								role={fullAddress ? "button" : undefined}
								tabIndex={fullAddress ? 0 : undefined}
								title={fullAddress ? "Click to copy address" : undefined}
							>
								{fullAddress || "Address not on file"}
								{addressCopied && <span className="propertyViewerCopiedBadge">Copied!</span>}
							</div>
							{gmapsUri && (
								<a
									href={gmapsUri}
									target="_blank"
									rel="noopener noreferrer"
									className="propertyViewerTableLink"
								>
									<PlaceIcon fontSize="inherit" /> Google Maps
								</a>
							)}
						</div>
					</Dialog>

					<div className="propertyViewerPaper">
						<div className="propertyViewerPaperHeader">
							<h3>Quick Links</h3>
						</div>
						<div className="propertyViewerPaperBody">
							<div className="propertyViewerQuickLinksGrid">
								<a
									href={hubspotUrl || undefined}
									target={hubspotUrl ? "_blank" : undefined}
									rel={hubspotUrl ? "noopener noreferrer" : undefined}
									className={`propertyViewerQuickLinkItem${!hubspotUrl ? " disabled" : ""}`}
								>
									<div className="propertyViewerIntegrationBadge hubspot">HS</div>
									<div className="propertyViewerQuickLinkTitle">HubSpot</div>
									<LaunchIcon fontSize="small" />
								</a>
								<a
									href={bqeUrl || undefined}
									target={bqeUrl ? "_blank" : undefined}
									rel={bqeUrl ? "noopener noreferrer" : undefined}
									className={`propertyViewerQuickLinkItem${!bqeUrl ? " disabled" : ""}`}
								>
									<div className="propertyViewerIntegrationBadge bqe">BQ</div>
									<div className="propertyViewerQuickLinkTitle">BQE Core</div>
									<LaunchIcon fontSize="small" />
								</a>
								<a
									href={dropboxUrl || undefined}
									target={dropboxUrl ? "_blank" : undefined}
									rel={dropboxUrl ? "noopener noreferrer" : undefined}
									className={`propertyViewerQuickLinkItem${!dropboxUrl ? " disabled" : ""}`}
								>
									<div className="propertyViewerIntegrationBadge dropbox">DB</div>
									<div className="propertyViewerQuickLinkTitle">Dropbox</div>
									<LaunchIcon fontSize="small" />
								</a>
								<a
									href={websiteUrl || undefined}
									target={websiteUrl ? "_blank" : undefined}
									rel={websiteUrl ? "noopener noreferrer" : undefined}
									className={`propertyViewerQuickLinkItem${!websiteUrl ? " disabled" : ""}`}
								>
									<div className="propertyViewerIntegrationBadge website">WWW</div>
									<div className="propertyViewerQuickLinkTitle">Property Website</div>
									<LaunchIcon fontSize="small" />
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	const renderJobsTab = () => (
		<div className="propertyViewerTabPanel">
			<div className="propertyViewerPaper">
				<div className="propertyViewerPaperHeader propertyViewerCardHeaderWithAction">
					<div>
						<h3>Jobs at This Property</h3>
						<p>Expand a job to see its phases</p>
					</div>
					<a
						className={`propertyViewerQuickLinkItem propertyViewerInlineDealLink${!bqeStructureUrl ? " disabled" : ""}`}
						href={bqeStructureUrl || undefined}
						target="_blank"
						rel="noreferrer"
					>
						<div className="propertyViewerIntegrationBadge bqe">BQ</div>
						<div className="propertyViewerQuickLinkTitle">BQE Structure</div>
						<LaunchIcon fontSize="small" />
					</a>
				</div>
				<div className="propertyViewerPaperBody propertyViewerCompactTableBody">
					{sectionStatus.jobs?.loading ? (
						<div className="propertyViewerEmptyStateMessage">Loading…</div>
					) : jobs.length === 0 ? (
						<div className="propertyViewerEmptyStateMessage">No BQE jobs found for this property.</div>
					) : (
						<table className="propertyViewerTable propertyViewerJobsTable">
							<thead>
								<tr>
									<th className="propertyViewerJobsColProject">Project</th>
									<th className="propertyViewerJobsColManager">Manager</th>
									<th className="propertyViewerJobsColStatus">Status</th>
									<th className="propertyViewerJobsColFinancials">Contract / Paid</th>
									<th className="propertyViewerJobsColPhases">Phases</th>
									<th className="propertyViewerJobsColChevron" />
								</tr>
							</thead>
							<tbody>
								{jobs.map((job, i) => {
									const hasPhases = job.phases.length > 0;
									const jobContractAmount = job.phases.reduce((sum, p) => sum + (p.contractAmount || 0), 0);
									const jobPaidAmount = job.phases.reduce((sum, p) => sum + (paidByPhaseId[p.id] || 0), 0);
									return (
										<React.Fragment key={job.id}>
											<tr
												className={hasPhases ? "propertyViewerTableRowClickable" : undefined}
												onClick={hasPhases ? () => toggleJob(i) : undefined}
											>
												<td><span className="propertyViewerStrongCell">{job.name}</span></td>
												<td>{job.manager || notApplicable}</td>
												<td><StatusChip status={job.status} /></td>
												<td>{formatCurrency(jobContractAmount)} / {formatCurrency(jobPaidAmount)}</td>
												<td>{job.phases.length}</td>
												<td className="propertyViewerJobsLinksCell">
													<a
														href={job.hubspotDealUrl || undefined}
														target={job.hubspotDealUrl ? "_blank" : undefined}
														rel={job.hubspotDealUrl ? "noopener noreferrer" : undefined}
														className={`propertyViewerIntegrationBadge propertyViewerHeroLinkBadge hubspot${!job.hubspotDealUrl ? " disabled" : ""}`}
														title={job.hubspotDealUrl ? "HubSpot Deal" : "HubSpot Deal Unavailable"}
														aria-label="HubSpot Deal"
														onClick={(e) => e.stopPropagation()}
													>
														HS
													</a>
													{hasPhases && (
														openJobs[i] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />
													)}
												</td>
											</tr>
											{openJobs[i] && hasPhases && (
												<tr className="propertyViewerPhaseRow">
													<td colSpan={6}>
														<div className="propertyViewerPhaseList">
															{job.phases.map((ph) => {
																// Only Active/On Hold phases still exist to open in
																// ProjectViewer — completed/inactive/canceled ones
																// stay informational, not a dead or misleading link.
																const isPhaseClickable = ph.status === "Active" || ph.status === "On Hold";
																const PhaseCardWrapper = isPhaseClickable ? "a" : "div";
																return (
																	<div className="propertyViewerPhaseCard" key={ph.id}>
																		<PhaseCardWrapper
																			{...(isPhaseClickable
																				? {
																					href: `/projects/viewer?recordId=${encodeURIComponent(ph.id)}`,
																					target: "_blank",
																					rel: "noopener noreferrer",
																				}
																				: {})}
																			className={`propertyViewerPhaseCardLink${isPhaseClickable ? " propertyViewerPhaseCardClickable" : ""}`}
																		>
																			<div>
														{ph.name}
														{isPhaseClickable && (
															<span className="propertyViewerPhaseClickHint">Click to open in Project Viewer</span>
														)}
													</div>
																			<div className="propertyViewerPhaseDates">
																				{formatDate(ph.startDate)} – {ph.completedOn ? formatDate(ph.completedOn) : "Present"}
																			</div>
																			<StatusChip status={ph.status} />
														<div className="propertyViewerPhaseFinancialValue">
															{formatCurrency(ph.contractAmount)} / {formatCurrency(paidByPhaseId[ph.id] || 0)}
														</div>
																		</PhaseCardWrapper>
																		<div className="propertyViewerPhaseQuickLinks">
																			{buildPhaseQuickLinkItems(ph).map((item) => (
																				<a
																					key={item.key}
																					href={item.url || undefined}
																					target={item.url ? "_blank" : undefined}
																					rel={item.url ? "noopener noreferrer" : undefined}
																					className={`propertyViewerIntegrationBadge propertyViewerHeroLinkBadge ${item.badgeClass}${!item.url ? " disabled" : ""}`}
																					title={item.url ? item.label : `${item.label} Unavailable`}
																					aria-label={item.label}
																				>
																					{item.short}
																				</a>
																			))}
																		</div>
																	</div>
																);
															})}
														</div>
													</td>
												</tr>
											)}
										</React.Fragment>
									);
								})}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);

	const renderFinancesTab = () => {
		const allInvoices = finances?.invoices || [];
		const outstandingInvoices = allInvoices.filter((inv) => inv.status === "Open" || inv.status === "Past Due");
		const draftInvoicesList = allInvoices.filter((inv) => inv.status === "Draft");
		const paidInvoicesList = allInvoices.filter((inv) => inv.status === "Paid");
		const invoiceListRows =
			invoiceListTab === "outstanding" ? outstandingInvoices : invoiceListTab === "draft" ? draftInvoicesList : paidInvoicesList;
		const invoiceEmptyMessage =
			invoiceListTab === "outstanding"
				? "No outstanding invoices."
				: invoiceListTab === "draft"
					? "No draft invoices."
					: "No paid invoices.";

		const invoicesSearchTerm = invoicesSearch.trim().toLowerCase();
		const invoiceRows = !invoicesSearchTerm
			? invoiceListRows
			: invoiceListRows.filter((inv) =>
				[inv.invoiceNumber, formatJobPath(toolsProperty?.name, inv.jobName, inv.phaseName), inv.status]
					.filter(Boolean)
					.join(" ")
					.toLowerCase()
					.includes(invoicesSearchTerm),
			);

		return (
			<div className="propertyViewerTabPanel">
				<div className="propertyViewerThreeColumnGrid propertyViewerFinTileGrid">
					{FIN_TILES.map((tile, i) => (
						<div className="propertyViewerPaper propertyViewerFinTile" key={tile.key}>
							<div className="propertyViewerFinTileLabel">{tile.label}</div>
							<div className={`propertyViewerFinTileValue${i === 3 && finances?.pastDue > 0 ? " danger" : ""}`}>
								{finances ? formatCurrency(finances[tile.key]) : notApplicable}
							</div>
							<div className="propertyViewerFinTileNote">{tile.note}</div>
						</div>
					))}
				</div>
				<div className="propertyViewerPaper">
					<div className="propertyViewerPaperHeader propertyViewerCardHeaderWithAction">
						<h3>BQE Invoices</h3>
						<div className="propertyViewerCardHeaderActions">
							<div className="propertyViewerMiniTabs" role="tablist">
								<button
									type="button"
									role="tab"
									aria-selected={invoiceListTab === "outstanding"}
									className={`propertyViewerMiniTab${invoiceListTab === "outstanding" ? " active" : ""}`}
									onClick={() => setInvoiceListTab("outstanding")}
								>
									Outstanding ({outstandingInvoices.length})
								</button>
								<button
									type="button"
									role="tab"
									aria-selected={invoiceListTab === "draft"}
									className={`propertyViewerMiniTab${invoiceListTab === "draft" ? " active" : ""}`}
									onClick={() => setInvoiceListTab("draft")}
								>
									Draft ({draftInvoicesList.length})
								</button>
								<button
									type="button"
									role="tab"
									aria-selected={invoiceListTab === "paid"}
									className={`propertyViewerMiniTab${invoiceListTab === "paid" ? " active" : ""}`}
									onClick={() => setInvoiceListTab("paid")}
								>
									Paid ({paidInvoicesList.length})
								</button>
							</div>
							<a
								className="propertyViewerQuickLinkItem propertyViewerInlineDealLink"
								href={BQE_INVOICE_REVIEW_URL}
								target="_blank"
								rel="noreferrer"
							>
								<div className="propertyViewerIntegrationBadge bqe">BQ</div>
								<div className="propertyViewerQuickLinkTitle">Invoice Review</div>
								<LaunchIcon fontSize="small" />
							</a>
						</div>
					</div>
					<div className="propertyViewerPaperBody propertyViewerCompactTableBody">
						{sectionStatus.finances?.loading ? (
							<div className="propertyViewerEmptyStateMessage">Loading…</div>
						) : !finances || allInvoices.length === 0 ? (
							<div className="propertyViewerEmptyStateMessage">No invoices found for this property.</div>
						) : invoiceListRows.length === 0 ? (
							<div className="propertyViewerEmptyStateMessage">{invoiceEmptyMessage}</div>
						) : (
							<>
								<div className="propertyViewerTableFilters">
									<input
										type="text"
										className="propertyViewerFilterSearch"
										placeholder="Search by invoice number, job, or status..."
										value={invoicesSearch}
										onChange={(e) => setInvoicesSearch(e.target.value)}
									/>
								</div>
								<table className="propertyViewerTable">
									<thead>
										<tr>
											<th>Invoice</th>
											<th>Job</th>
											<th>Date</th>
											<th>Amount</th>
											<th>Balance</th>
											<th>Status</th>
										</tr>
									</thead>
									<tbody>
										{invoiceRows.length === 0 ? (
											<tr>
												<td colSpan={6} className="propertyViewerEmptyCell">No invoices match your search.</td>
											</tr>
										) : (
											invoiceRows.map((inv) => (
												<tr key={inv.id}>
													<td className="propertyViewerMono">{inv.invoiceNumber || notApplicable}</td>
													<td>
														{inv.phaseId ? (
															<a
																href={`/projects/viewer?recordId=${encodeURIComponent(inv.phaseId)}&tab=invoices`}
																target="_blank"
																rel="noopener noreferrer"
																className="propertyViewerTableLink"
															>
																{formatJobPath(toolsProperty?.name, inv.jobName, inv.phaseName)}
															</a>
														) : (
															notApplicable
														)}
													</td>
													<td>{formatDate(inv.date)}</td>
													<td>{formatCurrency(inv.amount)}</td>
													<td>{formatCurrency(inv.balance)}</td>
													<td><StatusChip status={inv.status} /></td>
												</tr>
											))
										)}
									</tbody>
								</table>
							</>
						)}
					</div>
				</div>
			</div>
		);
	};

	const renderUnitsTab = () => {
		// Mining is a one-time action for a property with no units yet — while
		// that's true, the wizard is the only thing on this tab (no map, no
		// empty units table to look at). Push is real now, so units.length
		// (from the live HubSpot fetch) becomes accurate on its own once the
		// property's data is refreshed after a push — no local workaround needed.
		const hasUnits = units.length > 0;

		// units.length is 0 both before the fetch resolves AND for a genuinely
		// unit-less property — without this check, the tab would flash the
		// Unit Mining wizard first and then swap to the real units table the
		// instant data arrives for any property that actually has units.
		if (sectionStatus.units?.loading) {
			return (
				<div className="propertyViewerTabPanel">
					<div className="propertyViewerPaper">
						<div className="propertyViewerSkeletonBar propertyViewerSkeletonPulse" style={{ width: "30%", height: 20, margin: 20 }} />
						<div className="propertyViewerSkeletonBar propertyViewerSkeletonPulse" style={{ width: "60%", height: 14, margin: "0 20px 20px" }} />
						<div className="propertyViewerSkeletonBar propertyViewerSkeletonPulse" style={{ height: 260, margin: "0 20px 20px" }} />
					</div>
				</div>
			);
		}

		if (!hasUnits) {
			return (
				<div className="propertyViewerTabPanel">
					<UnitMiningWizard
						property={{
							id: toolsProperty?.id,
							name: toolsProperty?.name,
							addressLine1: hubspotFields.property_address_1,
							city: hubspotFields.property_city,
							state: hubspotFields.property_state,
							zip: hubspotFields.property_zip_code,
							location: propertyLocation,
						}}
						onFinish={handleRefreshProperty}
					/>
				</div>
			);
		}

		return (
			<div className="propertyViewerTabPanel">
				<div className="propertyViewerPaper">
					<div className="propertyViewerPaperHeader propertyViewerCardHeaderWithAction">
						<h3>Location</h3>
						<ToggleButtonGroup
							value={mapMode}
							exclusive
							size="small"
							color="primary"
							onChange={(e, val) => { if (val) setMapMode(val); }}
						>
							<ToggleButton value="m">Map</ToggleButton>
							<ToggleButton value="k">Satellite</ToggleButton>
						</ToggleButtonGroup>
					</div>
					<div className="propertyViewerMapFrame">
						{renderPropertyMap()}
						<IconButton
							size="small"
							className="propertyViewerMapExpandButton"
							onClick={() => { setActiveMapMarkerId(null); setMapDialogOpen(true); }}
							aria-label="Expand map"
							title="Expand map"
						>
							<FullscreenIcon fontSize="small" />
						</IconButton>
					</div>
					<div className="propertyViewerMapFooter">
						<div
							className={`propertyViewerMapAddress${fullAddress ? " propertyViewerCopyable" : ""}`}
							onClick={() => fullAddress && handleCopyAddress(fullAddress)}
							onKeyDown={(e) => { if (fullAddress && e.key === "Enter") handleCopyAddress(fullAddress); }}
							role={fullAddress ? "button" : undefined}
							tabIndex={fullAddress ? 0 : undefined}
							title={fullAddress ? "Click to copy address" : undefined}
						>
							{fullAddress || "Address not on file"}
							{addressCopied && <span className="propertyViewerCopiedBadge">Copied!</span>}
						</div>
						{gmapsUri && (
							<a
								href={gmapsUri}
								target="_blank"
								rel="noopener noreferrer"
								className="propertyViewerTableLink"
							>
								<PlaceIcon fontSize="inherit" /> Google Maps
							</a>
						)}
					</div>
				</div>

				<Dialog
					open={mapDialogOpen}
					onClose={() => { setActiveMapMarkerId(null); setMapDialogOpen(false); }}
					fullWidth
					maxWidth="lg"
					PaperProps={{ className: "propertyViewerMapDialogPaper" }}
				>
					<div className="propertyViewerMapDialogHeader">
						<div>
							<h3>{toolsProperty?.name}</h3>
						</div>
						<div className="propertyViewerMapDialogActions">
							<ToggleButtonGroup
								value={mapMode}
								exclusive
								size="small"
								color="primary"
								onChange={(e, val) => { if (val) setMapMode(val); }}
							>
								<ToggleButton value="m">Map</ToggleButton>
								<ToggleButton value="k">Satellite</ToggleButton>
							</ToggleButtonGroup>
							<IconButton onClick={() => { setActiveMapMarkerId(null); setMapDialogOpen(false); }} aria-label="Close">
								<CloseIcon />
							</IconButton>
						</div>
					</div>
					<div className="propertyViewerMapDialogFrame">
						{mapDialogOpen && renderPropertyMap()}
					</div>
					<div className="propertyViewerMapFooter propertyViewerMapDialogFooter">
						<div
							className={`propertyViewerMapAddress${fullAddress ? " propertyViewerCopyable" : ""}`}
							onClick={() => fullAddress && handleCopyAddress(fullAddress)}
							onKeyDown={(e) => { if (fullAddress && e.key === "Enter") handleCopyAddress(fullAddress); }}
							role={fullAddress ? "button" : undefined}
							tabIndex={fullAddress ? 0 : undefined}
							title={fullAddress ? "Click to copy address" : undefined}
						>
							{fullAddress || "Address not on file"}
							{addressCopied && <span className="propertyViewerCopiedBadge">Copied!</span>}
						</div>
						{gmapsUri && (
							<a
								href={gmapsUri}
								target="_blank"
								rel="noopener noreferrer"
								className="propertyViewerTableLink"
							>
								<PlaceIcon fontSize="inherit" /> Google Maps
							</a>
						)}
					</div>
				</Dialog>

				<div className="propertyViewerPaper">
					<div className="propertyViewerPaperHeader propertyViewerCardHeaderWithAction">
						<div>
							<h3>Units</h3>
							<p>{filteredUnits.length} shown</p>
						</div>
						<Button
							variant="outlined"
							size="small"
							disableElevation
							startIcon={<AddIcon />}
							onClick={openAddUnit}
							disabled={!toolsProperty?.hubspotId}
						>
							Add Unit
						</Button>
					</div>
					<div className="propertyViewerPaperBody propertyViewerCompactTableBody">
						{sectionStatus.units?.loading ? (
							<div className="propertyViewerEmptyStateMessage">Loading…</div>
						) : units.length === 0 ? (
							<div className="propertyViewerEmptyStateMessage">No units linked to this property in HubSpot.</div>
						) : (
							<>
								<div className="propertyViewerTableFilters">
									<input
										type="text"
										className="propertyViewerFilterSearch"
										placeholder="Search by unit, contact, or entity..."
										value={unitsSearch}
										onChange={(e) => setUnitsSearch(e.target.value)}
									/>
								</div>
								<table className="propertyViewerTable">
									<thead>
										<tr>
											<th>Unit</th>
											<th>Contact</th>
											<th>Entity</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody>
										{filteredUnits.length === 0 ? (
											<tr>
												<td colSpan={4} className="propertyViewerEmptyCell">No units match your search.</td>
											</tr>
										) : (
											filteredUnits.map((u) => (
												<tr key={u.id}>
													<td className="propertyViewerStrongCell">{u.name || notApplicable}</td>
													<td>
														{u.contacts && u.contacts.length > 0
															? u.contacts.map((c, i) => (
																<React.Fragment key={c.id}>
																	{i > 0 ? ", " : ""}
																	<a
																		href={HUBSPOT_CONTACT_RECORD_URL(c.id)}
																		target="_blank"
																		rel="noopener noreferrer"
																		className="propertyViewerTableLink"
																	>
																		{c.role ? `${c.name} (${c.role})` : c.name}
																	</a>
																</React.Fragment>
															))
															: notApplicable}
													</td>
													<td>
														{u.entities && u.entities.length > 0
															? u.entities.map((e, i) => (
																<React.Fragment key={e.id}>
																	{i > 0 ? ", " : ""}
																	<a
																		href={HUBSPOT_ENTITY_RECORD_URL(e.id)}
																		target="_blank"
																		rel="noopener noreferrer"
																		className="propertyViewerTableLink"
																	>
																		{e.role ? `${e.name} (${e.role})` : e.name}
																	</a>
																</React.Fragment>
															))
															: notApplicable}
													</td>
													<td>
														<div className="propertyViewerRowActions">
															<Tooltip arrow placement="top" title="Edit unit">
																<IconButton size="small" onClick={() => openEditUnit(u)} aria-label="Edit unit">
																	<EditIcon fontSize="small" />
																</IconButton>
															</Tooltip>
															<Tooltip arrow placement="top" title="Open in HubSpot">
																<a
																	href={HUBSPOT_UNIT_RECORD_URL(u.id)}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="propertyViewerTableIconLink"
																>
																	<LaunchIcon fontSize="small" />
																</a>
															</Tooltip>
														</div>
													</td>
												</tr>
											))
										)}
									</tbody>
								</table>
							</>
						)}
					</div>
				</div>
			</div>
		);
	};

	// Edit + open-in-HubSpot icons for one Contacts tab row.
	const renderLinkedRecordActions = (kind, record, hubspotRecordUrl) => (
		<div className="propertyViewerRowActions">
			<Tooltip arrow placement="top" title="Edit">
				<IconButton size="small" onClick={() => setEditingLinkedRecord({ kind, record })} aria-label="Edit">
					<EditIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip arrow placement="top" title="Open in HubSpot">
				<a href={hubspotRecordUrl} target="_blank" rel="noopener noreferrer" className="propertyViewerTableIconLink">
					<LaunchIcon fontSize="small" />
				</a>
			</Tooltip>
		</div>
	);

	const renderContactsTab = () => {
		const emptyMessage =
			contactsTabView === "contacts"
				? "No contacts linked to this property."
				: contactsTabView === "companies"
					? "No companies linked to this property."
					: "No entities linked to this property.";
		const activeCount =
			contactsTabView === "contacts" ? contacts.length : contactsTabView === "companies" ? companies.length : entities.length;

		return (
			<div className="propertyViewerTabPanel">
				<div className="propertyViewerPaper">
					<div className="propertyViewerPaperHeader propertyViewerCardHeaderWithAction">
						<h3>Contacts, Companies &amp; Entities</h3>
						<div className="propertyViewerCardHeaderActions">
							<div className="propertyViewerMiniTabs" role="tablist">
								<button
									type="button"
									role="tab"
									aria-selected={contactsTabView === "contacts"}
									className={`propertyViewerMiniTab${contactsTabView === "contacts" ? " active" : ""}`}
									onClick={() => setContactsTabView("contacts")}
								>
									Contacts ({contacts.length})
								</button>
								<button
									type="button"
									role="tab"
									aria-selected={contactsTabView === "companies"}
									className={`propertyViewerMiniTab${contactsTabView === "companies" ? " active" : ""}`}
									onClick={() => setContactsTabView("companies")}
								>
									Companies ({companies.length})
								</button>
								<button
									type="button"
									role="tab"
									aria-selected={contactsTabView === "entities"}
									className={`propertyViewerMiniTab${contactsTabView === "entities" ? " active" : ""}`}
									onClick={() => setContactsTabView("entities")}
								>
									Entities ({entities.length})
								</button>
							</div>
							<Button
								variant="outlined"
								size="small"
								disableElevation
								startIcon={<AddLinkIcon />}
								onClick={() => setLinksDialogOpen(true)}
								disabled={!toolsProperty?.hubspotId || sectionStatus.contacts?.loading}
							>
								Manage Links
							</Button>
							<a
								className={`propertyViewerQuickLinkItem propertyViewerInlineDealLink${!hubspotUrl ? " disabled" : ""}`}
								href={hubspotUrl || undefined}
								target="_blank"
								rel="noreferrer"
							>
								<div className="propertyViewerIntegrationBadge hubspot">HS</div>
								<div className="propertyViewerQuickLinkTitle">HubSpot Property</div>
								<LaunchIcon fontSize="small" />
							</a>
						</div>
					</div>
					<div className="propertyViewerPaperBody propertyViewerCompactTableBody">
						{sectionStatus.contacts?.loading ? (
							<div className="propertyViewerEmptyStateMessage">Loading…</div>
						) : activeCount === 0 ? (
							<div className="propertyViewerEmptyStateMessage">{emptyMessage}</div>
						) : (
							<>
								<div className="propertyViewerTableFilters">
									<input
										type="text"
										className="propertyViewerFilterSearch"
										placeholder="Search..."
										value={contactsSearch}
										onChange={(e) => setContactsSearch(e.target.value)}
									/>
								</div>
								{contactsTabView === "contacts" ? (
									<table className="propertyViewerTable">
										<thead>
											<tr>
												<th>Name</th>
												<th>Role</th>
												<th>Title</th>
												<th>Email</th>
												<th>Phone</th>
												<th>Mobile</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{filteredContacts.length === 0 ? (
												<tr>
													<td colSpan={7} className="propertyViewerEmptyCell">No contacts match your search.</td>
												</tr>
											) : (
												filteredContacts.map((ct) => (
													<tr key={ct.id}>
														<td>
															<div className="propertyViewerContactName">
																<span className="propertyViewerAvatar">
																	{ct.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
																</span>
																{ct.name}
															</div>
														</td>
														<td>{ct.role || notApplicable}</td>
														<td>{ct.jobTitle || notApplicable}</td>
														<td>
															{ct.email ? (
																<span
																	className="propertyViewerTableLink propertyViewerCopyable"
																	role="button"
																	tabIndex={0}
																	onClick={() => handleCopyEmail(ct.email)}
																	onKeyDown={(e) => { if (e.key === "Enter") handleCopyEmail(ct.email); }}
																	title="Click to copy email"
																>
																	{ct.email}
																	{copiedContactEmail === ct.email && <span className="propertyViewerCopiedBadge">Copied!</span>}
																</span>
															) : notApplicable}
														</td>
														<td className="propertyViewerMono">{ct.phone || notApplicable}</td>
														<td className="propertyViewerMono">{ct.mobilePhone || notApplicable}</td>
														<td>{renderLinkedRecordActions("contact", ct, HUBSPOT_CONTACT_RECORD_URL(ct.id))}</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								) : contactsTabView === "companies" ? (
									<table className="propertyViewerTable">
										<thead>
											<tr>
												<th>Name</th>
												<th>Role</th>
												<th>Type</th>
												<th>Domain</th>
												<th>Phone</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{filteredCompanies.length === 0 ? (
												<tr>
													<td colSpan={6} className="propertyViewerEmptyCell">No companies match your search.</td>
												</tr>
											) : (
												filteredCompanies.map((c) => (
													<tr key={c.id}>
														<td className="propertyViewerStrongCell">{c.name || notApplicable}</td>
														<td>{c.role || notApplicable}</td>
														<td>{c.type || notApplicable}</td>
														<td>
															{c.domain ? (
																<a
																	href={c.domain.startsWith("http") ? c.domain : `https://${c.domain}`}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="propertyViewerTableLink"
																>
																	{c.domain}
																</a>
															) : notApplicable}
														</td>
														<td className="propertyViewerMono">{c.phone || notApplicable}</td>
														<td>{renderLinkedRecordActions("company", c, HUBSPOT_COMPANY_RECORD_URL(c.id))}</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								) : (
									<table className="propertyViewerTable">
										<thead>
											<tr>
												<th>Name</th>
												<th>Role</th>
												<th>Type</th>
												<th>Address</th>
												<th>Phone</th>
												<th>Actions</th>
											</tr>
										</thead>
										<tbody>
											{filteredEntities.length === 0 ? (
												<tr>
													<td colSpan={6} className="propertyViewerEmptyCell">No entities match your search.</td>
												</tr>
											) : (
												filteredEntities.map((e) => (
													<tr key={e.id}>
														<td className="propertyViewerStrongCell">{e.name || notApplicable}</td>
														<td>{e.role || notApplicable}</td>
														<td>{e.type || notApplicable}</td>
														<td>{[e.address, e.city, e.state, e.zip].filter(Boolean).join(", ") || notApplicable}</td>
														<td className="propertyViewerMono">{e.phone || notApplicable}</td>
														<td>{renderLinkedRecordActions("entity", e, HUBSPOT_ENTITY_RECORD_URL(e.id))}</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		);
	};

	const renderActiveTab = () => {
		switch (activeTab) {
			case "jobs":
				return renderJobsTab();
			case "finances":
				return renderFinancesTab();
			case "units":
				return renderUnitsTab();
			case "contacts":
				return renderContactsTab();
			case "overview":
			default:
				return renderOverviewTab();
		}
	};

	return (
		<div className="PropertyViewer">
			<div className="propertyViewerPage">
				<div className={`propertyViewerWorkspace${browserPaneCollapsed ? " browserCollapsed" : ""}`}>
					<div className="propertyViewerBrowserPane propertyViewerPaper">
						<div className="propertyViewerPaperHeader propertyViewerBrowserHeader">
							<div>
								<h3>Property Viewer</h3>
							</div>
							{/* A span, not a div — the collapse CSS below hides direct `div`
							children of this header so these buttons stay usable while
							collapsed; a div wrapper here would hide them too. */}
							<span className="propertyViewerCardHeaderActions">
								{!browserPaneCollapsed && props.isAdmin && (
									<button
										type="button"
										className="propertyViewerBrowserToggle"
										aria-label="Add property"
										title="Add property"
										onClick={() => setAddPropertyOpen(true)}
									>
										<AddIcon fontSize="inherit" />
									</button>
								)}
								<button
									type="button"
									className="propertyViewerBrowserToggle"
									aria-label={browserPaneCollapsed ? "Expand property list" : "Collapse property list"}
									onClick={() => setBrowserPaneCollapsed((current) => !current)}
								>
									{browserPaneCollapsed ? <ChevronRightIcon fontSize="inherit" /> : <ChevronLeftIcon fontSize="inherit" />}
								</button>
							</span>
						</div>
						<div className={`propertyViewerPaperBody propertyViewerBrowserBody${browserPaneCollapsed ? " collapsed" : ""}`}>
							<div className="propertyViewerFilterStack">
								<input
									type="text"
									className="propertyViewerFilterInput"
									placeholder="Search properties"
									value={searchTerm}
									onChange={(event) => setSearchTerm(event.target.value)}
								/>
								<select
									className="propertyViewerFilterSelect"
									value={officeFilter}
									onChange={(event) => setOfficeFilter(event.target.value)}
								>
									{officeOptions.map((option) => (
										<option key={option} value={option}>{option}</option>
									))}
								</select>
							</div>
							<div className="propertyViewerPropertyList">
								{isListLoading ? (
									<div className="propertyViewerBrowserEmptyState">
										<p>Loading properties…</p>
									</div>
								) : listError ? (
									<div className="propertyViewerBrowserEmptyState">
										<InfoOutlinedIcon fontSize="inherit" />
										<p>{listError}</p>
									</div>
								) : filteredProperties.length === 0 ? (
									<div className="propertyViewerBrowserEmptyState">
										<InfoOutlinedIcon fontSize="inherit" />
										<p>No properties match your search.</p>
									</div>
								) : (
									filteredProperties.map((p) => (
										<button
											key={p.id}
											type="button"
											className={`propertyViewerPropertyListItem${String(p.id) === String(selectedId) ? " active" : ""}`}
											onClick={() => selectProperty(p.id)}
										>
											<div className="propertyViewerPropertyListTitle">{p.name}</div>
											<div className="propertyViewerPropertyListMeta">
												{[p.addressLine1, p.city].filter(Boolean).join(", ") || "No address on file"}
												{p.state ? ` · ${p.state}` : ""}
											</div>
										</button>
									))
								)}
							</div>
						</div>
					</div>

					<div className="propertyViewerDetailPane">
						{toolsProperty ? (
							<>
								<div className="propertyViewerHero propertyViewerPaper">
									<div className="propertyViewerHeroTop">
										<div className="propertyViewerHeroTitleBlock">
											<div className="propertyViewerEyebrowRow">
												<div className="propertyViewerEyebrow">Property</div>
												<div className="propertyViewerHeroNav">
													{renderQuickLinkIcons()}
													<div className="propertyViewerHeroNavDivider" />
													<button
														type="button"
														className="propertyViewerNavArrow"
														disabled={!prevProperty}
														onClick={() => prevProperty && selectProperty(prevProperty.id)}
														aria-label="Previous property"
													>
														<ChevronLeftIcon fontSize="inherit" />
													</button>
													<span className="propertyViewerNavPosition">
														{currentIndex + 1} / {filteredProperties.length}
													</span>
													<button
														type="button"
														className="propertyViewerNavArrow"
														disabled={!nextProperty}
														onClick={() => nextProperty && selectProperty(nextProperty.id)}
														aria-label="Next property"
													>
														<ChevronRightIcon fontSize="inherit" />
													</button>
													<button
														type="button"
														className={`propertyViewerNavArrow${isRefreshing ? " refreshing" : ""}`}
														disabled={isRefreshing}
														onClick={handleRefreshProperty}
														aria-label="Refresh property data"
														title={isRefreshing ? "Refreshing…" : "Refresh this property's data"}
													>
														<RefreshIcon fontSize="inherit" />
													</button>
													<div className="propertyViewerHeroNavDivider" />
													<button
														type="button"
														className="propertyViewerNavArrow"
														disabled={!toolsProperty?.hubspotId}
														onClick={openEditDetails}
														aria-label="Edit property details"
														title={toolsProperty?.hubspotId ? "Edit property details" : "No HubSpot record linked to this property"}
													>
														<EditIcon fontSize="inherit" />
													</button>
												</div>
											</div>
											<h1>{toolsProperty.name}</h1>
											<div className="propertyViewerMetaRow">
												<div className="propertyViewerMetaItem">
													<PlaceIcon fontSize="inherit" />
													<strong>{toolsProperty.state || notApplicable}</strong>
													<span>State</span>
												</div>
												{fullAddress && (
													<div className="propertyViewerMetaItem">
														<strong>{fullAddress}</strong>
														<span>Address</span>
													</div>
												)}
												{detail && (
													<div className="propertyViewerMetaItem">
														<strong>{jobs.length}</strong>
														<span>{jobs.length === 1 ? "Job" : "Jobs"}</span>
														{jobStatusBreakdown.map(({ status, count }) => (
															<React.Fragment key={status}>
																<span className="propertyViewerMetaDivider">|</span>
																<strong className={`propertyViewerMetaStatus ${JOB_STATUS_CLASS[status] || "pending"}`}>{count}</strong>
																<span className={`propertyViewerMetaStatus ${JOB_STATUS_CLASS[status] || "pending"}`}>{status}</span>
															</React.Fragment>
														))}
													</div>
												)}
											</div>
										</div>
									</div>

									{!allSectionsLoaded && (
										<div className="propertyViewerProgressBar">
											<div
												className="propertyViewerProgressBarFill"
												style={{ width: `${loadProgress}%` }}
											/>
										</div>
									)}
									<div className="propertyViewerTabs">
										{TABS.map((tab) => (
											<button
												key={tab.id}
												type="button"
												className={`propertyViewerTab${activeTab === tab.id ? " active" : ""}`}
												onClick={() => setActiveTab(tab.id)}
											>
												{tab.label}
												{tab.sectionKey && sectionStatus[tab.sectionKey]?.loading && (
													<span className="propertyViewerTabLoadingDot" />
												)}
												{tab.sectionKey && sectionStatus[tab.sectionKey]?.error && !sectionStatus[tab.sectionKey]?.loading && (
													<span className="propertyViewerTabErrorDot" title={sectionStatus[tab.sectionKey].error} />
												)}
											</button>
										))}
									</div>
								</div>

								{detailError ? (
									<div className="propertyViewerPaper">
										<div className="propertyViewerPaperBody">
											<div className="propertyViewerEmptyStateMessage">{detailError}</div>
										</div>
									</div>
								) : (
									renderActiveTab()
								)}

								{/* Record dialogs live here, outside the tab render functions,
								    so a tab re-rendering into a different branch (e.g. the Units
								    tab swapping between its table and skeleton) can't remount
								    and reset one mid-edit. */}
								<PropertyUnitDialog
									open={unitDialogOpen}
									unit={editingUnit}
									property={{
										id: toolsProperty?.id,
										name: toolsProperty?.name,
										addressLine1: hubspotFields.property_address_1,
										city: hubspotFields.property_city,
										state: hubspotFields.property_state,
										zip: hubspotFields.property_zip_code,
									}}
									existingUnits={units}
									employeeID={employeeID}
									onClose={() => setUnitDialogOpen(false)}
									onChanged={reloadUnits}
								/>

								<PropertyLinksDialog
									open={linksDialogOpen}
									property={{ id: toolsProperty?.id, name: toolsProperty?.name }}
									records={{ contacts, companies, entities }}
									employeeID={employeeID}
									onClose={() => setLinksDialogOpen(false)}
									onSaved={(section) => handleContactsSectionSaved(toolsProperty?.id, section)}
								/>

								{/* Keyed per record so the form's state starts from that
								    record — reusing one instance briefly kept the previous
								    record's fields, which don't exist on a different kind. */}
								<LinkedRecordEditDialog
									key={editingLinkedRecord ? `${editingLinkedRecord.kind}-${editingLinkedRecord.record.id}` : "none"}
									open={Boolean(editingLinkedRecord)}
									kind={editingLinkedRecord?.kind}
									record={editingLinkedRecord?.record}
									property={{ id: toolsProperty?.id, name: toolsProperty?.name }}
									employeeID={employeeID}
									onClose={() => setEditingLinkedRecord(null)}
									onSaved={(section) => handleContactsSectionSaved(toolsProperty?.id, section)}
								/>

								<Dialog open={editDetailsOpen} onClose={closeEditDetails} maxWidth="md" fullWidth>
									<DialogTitle className="propertyViewerEditDialogTitle">
										Edit Property Details
										{toolsProperty?.name && (
											<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
												{toolsProperty.name}
											</Typography>
										)}
									</DialogTitle>
									<DialogContent className="propertyViewerEditDialogContent">
										<div className="propertyViewerEditDialogGrid">
											{editableDetailFields.map(([label, key]) => (
												<TextField
													key={key}
													label={label}
													size="small"
													fullWidth
													multiline={key === "property_details"}
													minRows={key === "property_details" ? 3 : undefined}
													className={
														key === "property_details" || key === "property_name"
															? "propertyViewerEditDialogFieldWide"
															: undefined
													}
													value={editDetailsDraft[key] ?? ""}
													onChange={(e) => setEditDetailsDraft((current) => ({ ...current, [key]: e.target.value }))}
													disabled={editDetailsSaving}
												/>
											))}
										</div>

										{props.isAdmin && (
											<>
												<Divider />
												<div className="dialogSubtitle">J2 Tools Details</div>
												<div className="propertyViewerEditDialogGrid">
													<TextField
														select
														size="small"
														fullWidth
														className="propertyViewerEditDialogFieldWide"
														label="OIC"
														value={oicDraft}
														onChange={(e) => setOicDraft(e.target.value)}
														disabled={editDetailsSaving}
													>
														<MenuItem value="OR">OR</MenuItem>
														<MenuItem value="WA">WA</MenuItem>
														<MenuItem value="UT">UT</MenuItem>
													</TextField>
													<Autocomplete
														size="small"
														className="propertyViewerEditDialogFieldWide"
														options={bqePropertyOptions}
														getOptionLabel={(option) => option.displayName ?? option}
														filterSelectedOptions
														value={
															bqePropertyRecord && Object.keys(bqePropertyRecord).length > 0
																? bqePropertyRecord
																: ""
														}
														onChange={bqePropertyNameSelected}
														clearOnBlur={false}
														loading={bqePropertySearchLoading}
														disabled={editDetailsSaving}
														renderInput={(params) => (
															<TextField
																{...params}
																size="small"
																label="BQE Property"
																placeholder="Search BQE properties"
																value={bqePropertySearchValue || ""}
																onKeyUp={bqePropertyNameChanged}
																InputProps={{
																	...params.InputProps,
																	endAdornment: (
																		<>
																			{bqePropertySearchLoading ? (
																				<CircularProgress color="inherit" size={16} />
																			) : null}
																			{params.InputProps.endAdornment}
																		</>
																	),
																}}
															/>
														)}
													/>
													<div className="propertyViewerEditDialogFieldWide">
														<DropboxFileSelector filePath={dropboxPathDraft} setFilePath={setDropboxPathDraft} />
													</div>
												</div>
											</>
										)}

										{editDetailsError && (
											<Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
												{editDetailsError}
											</Typography>
										)}
										<div className="dialogButtons">
											<Button variant="outlined" color="primary" onClick={closeEditDetails} disabled={editDetailsSaving}>
												Cancel
											</Button>
											<Button
												variant="contained"
												color="primary"
												disableElevation
												onClick={saveEditDetails}
												disabled={editDetailsSaving}
												startIcon={editDetailsSaving ? <CircularProgress size={14} color="inherit" /> : null}
											>
												{editDetailsSaving ? "Saving…" : "Save"}
											</Button>
										</div>
									</DialogContent>
								</Dialog>

								<Dialog open={enrichSavePromptOpen} onClose={declineEnrichSavePrompt} maxWidth="sm" fullWidth>
									<DialogTitle className="propertyViewerEditDialogTitle">Regenerate Description?</DialogTitle>
									<DialogContent className="propertyViewerEditDialogContent">
										<Typography variant="body2" sx={{ mb: 1 }}>
											You updated these fields, which the property description is grounded on:
										</Typography>
										<ul className="propertyViewerEnrichFieldList">
											{enrichSavePromptFieldLabels.map((label) => (
												<li key={label}>{label}</li>
											))}
										</ul>
										<Typography variant="body2" sx={{ mb: 2 }}>
											Regenerate the description using these new values?
										</Typography>
										<div className="dialogButtons">
											<Button variant="outlined" color="primary" onClick={declineEnrichSavePrompt}>
												Don't Regenerate
											</Button>
											<Button variant="contained" color="primary" disableElevation onClick={acceptEnrichSavePrompt}>
												Regenerate
											</Button>
										</div>
									</DialogContent>
								</Dialog>

								<PropertyEnrichRunDialog
									open={enrichDialogOpen}
									isFirstEnrich={isFirstEnrich}
									running={enrichRunning}
									error={enrichError}
									onClose={closeEnrichRunDialog}
									onCancelRun={cancelEnrichRun}
									onRunFull={() => runEnrich("full")}
									onRunResearch={() => runEnrich("research")}
									onRunDescription={() => runEnrich("description")}
								/>

								<PropertyEnrichReviewDialog
									open={enrichReviewOpen}
									changes={enrichChanges}
									infoMessages={enrichReviewErrors}
									selections={enrichSelections}
									onSelectionChange={(key, val) => setEnrichSelections((current) => ({ ...current, [key]: val }))}
									onCancel={closeEnrichReview}
									onConfirm={confirmEnrichReview}
									onRetry={retryEnrichReview}
									saving={enrichConfirmSaving}
									error={enrichError}
								/>
							</>
						) : (
							<div className="propertyViewerPaper">
								<div className="propertyViewerPaperBody">
									<div className="propertyViewerEmptyStateMessage">
										{isListLoading
											? "Loading properties…"
											: listError
												? listError
												: "Select a property to view its details."}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
				<PropertyConsoleEditAddDialog
					open={addPropertyOpen}
					editing={false}
					recordToEdit={{}}
					onClose={closeAddPropertyAndRefresh}
					onCloseNoRefresh={closeAddProperty}
				/>
			</div>
		</div>
	);
};

// EXPORT
export default PropertyViewer;
